<template>
  <div ref="host" class="island-playground-three" />
</template>

<script>
import * as THREE from 'three'
import message from '@/utils/message'
import {
  cloneSandModel,
  disposeSandProp,
  fixVolumetricFishFacing,
  ensureLayFlatCritterPose,
  getSandPropBottomLocalY,
  getSandPropFootprintRadius,
  loadSandModel,
  preloadSandModels,
  resetSandPropPose,
  updateSandPropAnimations
} from '@/utils/sandModelLoader'
import { getSandModelSpec, SAND_MODEL_PRELOAD } from '@/config/sandModels'
import { getSandItemKey as buildSandItemKey } from '@/config/sandCatalog'
import { findSandCatalogItem } from '@/utils/sandTableRestore'
import { SAND_CATEGORY_MAP } from '@/utils/sandTableSave'

const WATER_VS = `
uniform float uTime;
varying vec2 vUv;
varying float vWave;
void main() {
  vUv = uv;
  vec3 p = position;
  float w =
    sin(p.x * 2.15 + uTime * 1.18) * 0.5 +
    cos(p.z * 2.55 + uTime * 0.92) * 0.5;
  p.y += w * 0.34;
  vWave = w;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
}
`

const WATER_FS = `
uniform float uTime;
uniform vec3 uColor;
uniform vec3 uDeep;
uniform vec3 uHighlight;
varying vec2 vUv;
varying float vWave;
void main() {
  vec2 uv = vUv * 96.0;
  float t = uTime * 1.12;
  float cell = sin(uv.x + t * 0.64) * sin(uv.y * 1.02 - t * 0.5);
  float cell2 = sin(uv.x * 1.35 + uv.y * 0.9 - t * 0.38) * 0.85;
  float rip = clamp(cell * 0.55 + cell2 * 0.4, -1.0, 1.0) * 0.5 + 0.5;
  float fine = sin(uv.x * 44.0 + t * 0.88) * sin(uv.y * 40.0 - t * 0.72);
  float glint = max(0.0, fine) * max(0.0, fine);
  vec3 col = mix(uDeep, uColor, 0.34 + rip * 0.56 + vWave * 0.07);
  col = mix(col, uHighlight, 0.05 + fine * 0.045 + glint * 0.09);
  gl_FragColor = vec4(col, 1.0);
}
`

/** 沙盘堆沙/水面/材质共用网格 */
const SAND_TERRAIN_VS = `
attribute float wetness;
attribute float matType;
attribute vec3 color;
varying vec3 vNormal;
varying float vWet;
varying float vMat;
varying float vHeight;
varying vec3 vColorAttrib;
varying vec3 vWorldPos;

void main() {
  vWet = wetness;
  vMat = matType;
  vHeight = position.y;
  vColorAttrib = clamp(color, 0.0, 1.0);
  vNormal = normalize(normalMatrix * normal);
  vec4 wp = modelMatrix * vec4(position, 1.0);
  vWorldPos = wp.xyz;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`

const SAND_TERRAIN_FS = `
uniform vec3 uAmbient;
uniform vec3 uSunColor;
uniform vec3 uLightDir;
uniform vec3 uMatGrass;
uniform vec3 uMatSand;
uniform vec3 uMatSnow;
uniform vec3 uMatBrick;
uniform vec3 uMatRock;
uniform vec3 uWaterBody;
uniform vec3 uWaterGlow;
uniform float uTime;
uniform float uTourInWater;
uniform float uLightMul;

varying vec3 vNormal;
varying float vWet;
varying float vMat;
varying float vHeight;
varying vec3 vColorAttrib;
varying vec3 vWorldPos;

vec3 terrainMatColor(float t) {
  t = clamp(t, 0.0, 4.0);
  float i = floor(t + 0.001);
  if (i < 0.5) return uMatGrass;
  if (i < 1.5) return uMatSand;
  if (i < 2.5) return uMatSnow;
  if (i < 3.5) return uMatBrick;
  return uMatRock;
}

// 程序化砖块纹理：沿y分层错缝，接缝处变暗模拟灰泥
float brickPattern(vec2 p) {
  vec2 s = p * vec2(4.0, 6.0);
  float row = floor(s.y);
  s.x += mod(row, 2.0) * 0.5;
  vec2 cell = fract(s);
  float mortarX = smoothstep(0.0, 0.07, cell.x) * (1.0 - smoothstep(0.93, 1.0, cell.x));
  float mortarY = smoothstep(0.0, 0.09, cell.y) * (1.0 - smoothstep(0.91, 1.0, cell.y));
  return clamp(mortarX * mortarY, 0.0, 1.0);
}

// 程序化岩石纹理：多阶噪声叠加模拟凹凸岩石面
float rockPattern(vec2 p) {
  float n = 0.0;
  n += abs(sin(p.x * 9.1 + p.y * 7.3));
  n += abs(sin(p.x * 17.4 - p.y * 13.2) * 0.5);
  n += abs(sin(p.x * 31.8 + p.y * 29.6) * 0.25);
  n = n / 1.75;
  n = pow(n, 1.6);
  return n;
}

vec3 terrainMatColorSmooth(float t) {
  t = clamp(t, 0.0, 4.0);
  float i0 = floor(t + 0.0001);
  float f = t - i0;
  if (f < 0.001 || i0 >= 4.0) return terrainMatColor(i0);
  f = f * f * (3.0 - 2.0 * f);
  return mix(terrainMatColor(i0), terrainMatColor(i0 + 1.0), f);
}

// 带程序纹理的材质色：仅岩石加凹凸，草地/沙地/雪地/砖块保持纯色
vec3 terrainMatTextured(float t, vec2 texCoord) {
  t = clamp(t, 0.0, 4.0);
  float i = floor(t + 0.001);
  vec3 base;
  if (i < 0.5) {
    base = uMatGrass;
  } else if (i < 1.5) {
    base = uMatSand;
  } else if (i < 2.5) {
    base = uMatSnow;
  } else if (i < 3.5) {
    base = uMatBrick;
  } else {
    float r = rockPattern(texCoord);
    base = uMatRock * (0.76 + r * 0.48);
  }
  return base;
}

void main() {
  float w = clamp(vWet, 0.0, 1.0);
  bool isPool = w > 0.12;
  bool isFloor = isPool && vHeight < -0.006;
  bool isWall = isPool && !isFloor && vHeight < 0.012;

  float groundMat = vMat;
  if (isPool && !isFloor) groundMat = 0.0;

  float heightEdit = smoothstep(0.0, 0.014, abs(vHeight));
  float matBlend = smoothstep(0.0, 0.72, groundMat);
  float matEdit = matBlend * (isFloor ? 1.0 : (1.0 - smoothstep(0.05, 0.12, w)));
  float poolWallEdit = isWall ? smoothstep(0.12, 0.38, w) : 0.0;
  float editMask = max(heightEdit, max(matEdit, poolWallEdit));
  bool isFlatGrass = abs(vHeight) < 0.002 && groundMat < 0.08 && w < 0.12;
  if (isFlatGrass) discard;
  if (editMask < 0.03 && matBlend < 0.03) discard;

  vec3 N = normalize(vNormal);
  vec3 L = normalize(uLightDir);
  float diff = max(dot(N, L), 0.0);
  float up = max(dot(N, vec3(0.0, 1.0, 0.0)), 0.0);
  vec3 grassCol = uMatGrass * vColorAttrib + vec3(0.02, 0.09, 0.04);
  vec2 texCoord = vWorldPos.xz;
  vec3 matCol = terrainMatTextured(isFloor ? vMat : groundMat, texCoord) * vColorAttrib;
  vec3 base = mix(grassCol, matCol, matBlend);
  bool isGrass = !isFloor && !isWall && matBlend < 0.08;
  if (isFloor && matBlend < 0.1) {
    base *= vec3(0.52, 0.66, 0.8);
    if (uTourInWater > 0.5) base *= 1.18;
  } else if (isWall && matBlend < 0.1) {
    base = mix(uMatSand, uMatGrass, 0.35) * vColorAttrib * vec3(0.68, 0.72, 0.62);
  }
  float shade = isGrass
    ? max(0.86, 0.74 + 0.22 * diff + 0.08 * up)
    : (0.62 + 0.38 * diff);
  vec3 col = base * shade;
  col *= clamp(uLightMul, 0.0, 1.0);
  if (isPool && vHeight > -0.008 && !isWall) discard;
  float alpha = clamp(max(editMask, matBlend * 0.92), 0.0, 1.0);
  if (heightEdit > 0.02) alpha = 1.0;
  gl_FragColor = vec4(col, alpha);
}
`

const POOL_WATER_SURFACE_VS = `
attribute float wetness;
varying float vWet;
varying vec3 vWorldPos;
void main() {
  vWet = wetness;
  vec4 wp = modelMatrix * vec4(position, 1.0);
  vWorldPos = wp.xyz;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`

const POOL_WATER_SURFACE_FS = `
uniform float uTime;
uniform vec3 uWaterBody;
uniform vec3 uWaterGlow;
uniform float uTourWade;
uniform float uTourLookDown;
uniform float uLightMul;
varying float vWet;
varying vec3 vWorldPos;
void main() {
  float w = clamp(vWet, 0.0, 1.0);
  if (w < 0.16) discard;
  // 边缘按湿痕渐变，内部完全不依赖地形高度，避免水面内的分级台阶感
  float shoreMask = 1.0;
  if (w < 0.55) {
    shoreMask *= smoothstep(0.14, 0.46, w);
  }
  float rip = sin(vWorldPos.x * 38.0 + uTime * 1.1) * sin(vWorldPos.z * 36.0 - uTime * 0.9);
  rip = rip * 0.5 + 0.5;
  vec3 col = mix(uWaterBody, uWaterGlow, 0.2 + rip * 0.14);
  col *= clamp(uLightMul, 0.0, 1.0);
  float alpha = shoreMask * 0.68;
  // 水面边缘过渡区：接近水面但不是全湿的碎片也混入水色，避免陆地色透边
  if (w > 0.06 && w < 0.16) {
    float t = (w - 0.06) / 0.10;
    col = mix(col * 0.72, col, t * t);
    alpha = max(alpha, t * 0.45);
  }
  alpha *= mix(1.0, 0.28, clamp(uTourWade, 0.0, 1.0) * clamp(uTourLookDown, 0.0, 1.0));
  if (alpha < 0.02) discard;
  gl_FragColor = vec4(col, alpha);
}
`

/** 程序化草点纹理：透明底 + 随机深/浅绿点，用于顶面多层叠加 */
function createGrassDotTexture ({
  canvasSize,
  seed,
  dotCount,
  radiusMin,
  radiusMax,
  darkRgb = '22, 101, 52',
  lightRgb = '134, 239, 172'
}) {
  const canvas = document.createElement('canvas')
  canvas.width = canvas.height = canvasSize
  const ctx = canvas.getContext('2d')
  let state = (seed >>> 0) || 1
  const rnd = () => {
    state = Math.imul(state ^ (state >>> 15), state | 1)
    state ^= state + Math.imul(state ^ (state >>> 7), state | 61)
    return (state >>> 0) / 4294967296
  }
  for (let i = 0; i < dotCount; i++) {
    const x = rnd() * canvasSize
    const y = rnd() * canvasSize
    const r = radiusMin + rnd() * (radiusMax - radiusMin)
    const light = rnd() > 0.58
    const baseA = light ? 0.14 + rnd() * 0.22 : 0.18 + rnd() * 0.32
    const a = baseA * (0.75 + rnd() * 0.35)
    ctx.fillStyle = light
      ? `rgba(${lightRgb},${a})`
      : `rgba(${darkRgb},${a})`
    ctx.beginPath()
    ctx.arc(x, y, r, 0, Math.PI * 2)
    ctx.fill()
  }
  const tex = new THREE.CanvasTexture(canvas)
  tex.wrapS = THREE.RepeatWrapping
  tex.wrapT = THREE.RepeatWrapping
  tex.minFilter = THREE.LinearMipmapLinearFilter
  tex.magFilter = THREE.LinearFilter
  tex.generateMipmaps = true
  tex.needsUpdate = true
  return tex
}

/** 沙具：预览图 PNG（竖立平面，支持俯仰/左右，不用 Sprite） */
function createPreviewImageSprite (url) {
  return new Promise((resolve) => {
    if (!url) {
      resolve(null)
      return
    }
    new THREE.TextureLoader().load(
      url,
      (texture) => {
        if (THREE.sRGBEncoding) texture.encoding = THREE.sRGBEncoding
        texture.needsUpdate = true
        const img = texture.image
        const aspect = img && img.width > 0 ? img.width / img.height : 1
        const h = 0.72
        const w = h * aspect
        const geo = new THREE.PlaneGeometry(w, h)
        geo.translate(0, h / 2, 0)
        const mat = new THREE.MeshLambertMaterial({
          map: texture,
          transparent: true,
          alphaTest: 0.08,
          side: THREE.DoubleSide,
          depthWrite: true
        })
        const plane = new THREE.Mesh(geo, mat)
        plane.castShadow = false
        plane.userData.sandPropKind = 'preview-plane'
        resolve(plane)
      },
      undefined,
      () => resolve(null)
    )
  })
}

/** 沙具：emoji 贴图精灵 */
function createEmojiSprite (emoji, canvasSize = 128) {
  const canvas = document.createElement('canvas')
  canvas.width = canvas.height = canvasSize
  const ctx = canvas.getContext('2d')
  if (!ctx) return null
  ctx.clearRect(0, 0, canvasSize, canvasSize)
  ctx.font = `${Math.round(canvasSize * 0.72)}px "Apple Color Emoji", "Segoe UI Emoji", sans-serif`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(emoji, canvasSize / 2, canvasSize / 2)
  const map = new THREE.CanvasTexture(canvas)
  map.minFilter = THREE.LinearFilter
  map.magFilter = THREE.LinearFilter
  map.needsUpdate = true
  const mat = new THREE.SpriteMaterial({
    map,
    transparent: true,
    depthTest: true,
    depthWrite: false,
    fog: false
  })
  const sprite = new THREE.Sprite(mat)
  sprite.scale.set(0.36, 0.36, 1)
  sprite.userData._emojiCanvas = canvas
  return sprite
}

/**
 * 沙盘材质基色不变；天气变化时仅整体明暗，不使用阴影贴图。
 */
const LIGHT_DAY_BASE = {
  hemiSky: 0xe3f7e8,
  hemiGround: 0x1a4d2a,
  hemiIntensity: 0.95,
  sunColor: 0xffffff,
  sunIntensity: 1.02
}

/** 草台顶面基色；天气变化时仅整体乘明暗，不做 Lambert/阴影 */
const GRASS_TOP_BASE = 0x38d96a

const WEATHER_PRESETS = {
  'day-clear': {
    bg: 0x90caf9,
    fog: [0x90caf9, 28, 85],
    precip: null,
    lightMul: 1
  },
  'day-cloudy': {
    bg: 0xb0bec5,
    fog: [0xb0bec5, 16, 68],
    precip: null,
    lightMul: 0.80
  },
  'day-rain': {
    bg: 0x78909c,
    fog: [0x546e7a, 10, 52],
    precip: 'rain',
    lightMul: 0.88
  },
  'day-snow': {
    bg: 0xb0c4de,
    fog: [0x90a4ae, 12, 50],
    precip: 'snow',
    lightMul: 0.92
  },
  'night-clear': {
    bg: 0x1a237e,
    fog: [0x0d1642, 8, 58],
    precip: null,
    lightMul: 0.38
  },
  'night-cloudy': {
    bg: 0x263238,
    fog: [0x1c2429, 6, 44],
    precip: null,
    lightMul: 0.28
  },
  'night-rain': {
    bg: 0x1b2226,
    fog: [0x141a1e, 4, 36],
    precip: 'rain',
    lightMul: 0.22
  },
  'night-snow': {
    bg: 0x283593,
    fog: [0x1a237e, 7, 46],
    precip: 'snow',
    lightMul: 0.32
  }
}

const YAW_SENS = 0.0085
const PITCH_SENS = 0.0085
const ORBIT_MIN_RADIUS = 2.2
const ORBIT_MAX_RADIUS = 18
const CAMERA_MIN_ABOVE_TARGET = 0.42
/** 默认 orbit：正面、俯角 45°（正对桌面）— Cursor Write It */
const CAMERA_DEFAULT_RADIUS = 9.2
/** 手机端：原 PC 默认视距作为最远缩放，进入时更近 */
const MOBILE_ORBIT_MAX_RADIUS = 9.2
const MOBILE_CAMERA_DEFAULT_RADIUS = 5.2
const CAMERA_DEFAULT_ELEVATION = Math.PI / 4
const CAMERA_DEFAULT_AZIMUTH = 0
/** 游览：贴沙盘地表行走 — Cursor Write It */
const TOUR_EYE_HEIGHT = 0.14
const TOUR_MOVE_SPEED = 1.05
const TOUR_LOOK_SENS = 0.0032
const TOUR_ENTER_DURATION = 2.4
const TOUR_EXIT_DURATION = 1.8
const TOUR_PITCH_MIN = -1.12
const TOUR_PITCH_MIN_WATER = -1.42
const TOUR_PITCH_MAX = 1.05
const TOUR_EDGE_INSET = 0.08
/** 草地沙盘尺寸（世界单位）：X=左右宽度，Z=前后深度 */
const ISLAND_DEPTH = 4.2
const ISLAND_WIDTH = 6.0
const ISLAND_HALF_X = ISLAND_WIDTH * 0.5
const ISLAND_HALF_Z = ISLAND_DEPTH * 0.5
/** 草面与堆沙网格共用缩进（原 0.96/0.968 不一致，外圈留空过大） */
const GRASS_TOP_INSET = 0.992
/** 地形笔划在沙面顶上的采样间距（世界单位），避免快速拖动时只在端点打点、中间留空 */
const TERRAIN_STROKE_SAMPLE_STEP = 0.055
const TERRAIN_STROKE_MAX_SAMPLES = 64

/** 堆沙：细分网格笔刷 */
const SCULPT_GRID_SEG = 200
const SCULPT_PLANE_INSET = GRASS_TOP_INSET
const SCULPT_BRUSH_R = 0.4
const SCULPT_MAX_Y = 0.58
const SCULPT_BASE_LIFT = 0.0092
/** 侧壁：高度超过此值才生成围合，避免平地也出墙 */
const SCULPT_SIDE_MIN_H = 0.0
/** 侧壁：单条边顶点高度至少到此值才出墙，避免沙堆外缘一圈横纹 */
const SCULPT_SIDE_EDGE_MIN_H = 0.022
/** 相机俯仰大于此角度（越俯视越大）时隐藏侧壁，避免顶视出现格子横纹 */
const SCULPT_SIDE_PITCH_SHOW_MAX = 38
const SCULPT_SIDE_COLOR = 0x2e8f4a
/** 地形 sculpt 最低高度（水面挖坑可低于 0）— Cursor Write It */
const SCULPT_MIN_Y = 0

/** 水面：向下挖坑 + 平面半透明水层 */
const WATER_MAX_DEPTH = 0.22
const WATER_DIG_PULL = 0.38
const WATER_SURFACE_Y = 0.002
/** 高于此高度的地形视为陆地/山体，不参与挖池与水面渲染 */
const WATER_MAX_TERRAIN_Y = WATER_SURFACE_Y + 0.012
const WATER_BRUSH_R_MUL = 0.78
const WATER_SIGMA_MUL = 0.4
const WATER_WET_STAMP_BASE = 0.22
const TOUR_WATER_WADE_WET = 0.15
const TOUR_WATER_EYE_OFFSET = 0.09
const TOUR_WATER_WADE_BLEND = 9
/** 后端 terrainData.h 通常只存 3~6 位小数：wetness 用 0.0001，材质 matType 用 0.000001 */
const TERRAIN_WET_H_FACTOR = 0.0001
const TERRAIN_MAT_H_FACTOR = 0.000001

function encodeTerrainHeightForSave (height, wetness, matType) {
  const h = Math.max(-WATER_MAX_DEPTH, Math.min(SCULPT_MAX_Y, height || 0))
  const w = Math.max(0, Math.min(1, wetness || 0))
  const m = Math.max(0, Math.min(4, Math.round(matType || 0)))
  const hasW = w > 0.03
  const hasM = m > 0
  if (!hasW && !hasM) return h.toFixed(3)
  let encoded = h
  if (hasW) encoded += w * TERRAIN_WET_H_FACTOR
  if (hasM) encoded += m * TERRAIN_MAT_H_FACTOR
  const decimals = hasW || hasM ? 6 : 3
  return encoded.toFixed(decimals)
}

function safeFixed (value, digits) {
  const n = Number(value)
  return (Number.isFinite(n) ? n : 0).toFixed(digits)
}

function decodeTerrainHeightFromSave (stored) {
  const v = typeof stored === 'number' ? stored : parseFloat(stored)
  if (isNaN(v)) return { h: 0, w: 0, m: 0 }
  const h = Math.round(v * 1000) / 1000
  const rem = v - h
  let m = 0
  const mCandidate = Math.round(rem / TERRAIN_MAT_H_FACTOR)
  if (mCandidate >= 1 && mCandidate <= 4) {
    m = mCandidate
  }
  const wRem = rem - m * TERRAIN_MAT_H_FACTOR
  let w = wRem / TERRAIN_WET_H_FACTOR
  if (w < 0.03) w = 0
  else w = Math.min(1, w)
  return {
    h: Math.max(-WATER_MAX_DEPTH, Math.min(SCULPT_MAX_Y, h)),
    w,
    m
  }
}

/** 平整：高斯柔边抹平堆沙/水面，保留地表材质 */
const FLATTEN_BRUSH_R_MUL = 0.78
const FLATTEN_SIGMA_MUL = 0.4
const FLATTEN_HEIGHT_RELAX = 0.11
const FLATTEN_WET_RELAX = 0.22
const FLATTEN_COLOR_RELAX = 0.055
const FLATTEN_WET_ZERO = 0.06
const FLATTEN_HEIGHT_ZERO = 0.004

/** 材质笔刷：顶点 matType 0=草地 1=沙地 2=雪地 3=砖块 4=岩石 */
const MATERIAL_TYPE_INDEX = {
  grass: 0,
  sand: 1,
  snow: 2,
  brick: 3,
  rock: 4
}
const MATERIAL_BRUSH_R_MUL = 0.55
const MATERIAL_BLEND_BASE = 1.0
const MATERIAL_SIGMA_MUL = 0.6
const MATERIAL_CUT_MUL = 1.2

/** 沙具编辑 */
const PROP_BASE_SCALE = 0.36
const PROP_SCALE_STEP = 1.12
const PROP_ROTATE_STEP = Math.PI / 12
const PROP_HEIGHT_STEP = 0.028
/** 俯仰默认上限（弧度，±90°）；个别模型可在 spec.propPitchMax 放宽 — Cursor Write It */
const PROP_PITCH_MAX = Math.PI / 2

function getEditingPropSpec (prop) {
  if (!prop) return null
  if (prop.userData.sandModelSpec) return prop.userData.sandModelSpec
  const ref =
    prop.userData.sandProp &&
    (prop.userData.sandProp.modelKey || prop.userData.sandProp.modelUrl)
  return ref ? getSandModelSpec(ref) : null
}

function resolvePropPitchMax (spec) {
  if (spec && typeof spec.propPitchMax === 'number' && spec.propPitchMax > 0) {
    return spec.propPitchMax
  }
  return PROP_PITCH_MAX
}
const PROP_SCALE_MIN = 0.35
const PROP_SCALE_MAX = 3.2
/** 沙具高度偏移：与沙盘 sculpt 范围一致（0=贴地，+50=最高，-10=挖至最低）— Cursor Write It */
const PROP_HEIGHT_MIN = SCULPT_MIN_Y - SCULPT_MAX_Y
const PROP_HEIGHT_MAX = SCULPT_MAX_Y
/** 沙具底面相对草面的微小抬升，避免与绿地 z-fighting 发白 */
let _sandPropIdSeq = 0

export default {
  name: 'IslandPlaygroundThree',
  props: {
    terrainVisible: {
      type: Boolean,
      default: true
    },
    weather: {
      type: String,
      default: 'day-clear'
    },
    terrainTool: {
      type: String,
      default: null
    },
    terrainMaterial: {
      type: String,
      default: 'grass'
    },
    /** 选中的沙具：{ label, emoji, category, key }，非空时点击沙盘放置 */
    sandPlaceItem: {
      type: Object,
      default: null
    },
    /** 禁用 pointer capture（当滑条编辑激活时使用，防止触控冲突） */
    pointerCaptureDisabled: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      scene: null,
      camera: null,
      renderer: null,
      waterUniforms: null,
      sandboxRoot: null,
      terrainGroup: null,
      raf: 0,
      ro: null,
      onResize: null,
      disposed: false,
      lastTickMs: 0,
      hemiLight: null,
      dirLight: null,
      rainPoints: null,
      snowPoints: null,
      precipMode: null
    }
  },
  watch: {
    terrainVisible (v) {
      if (this.terrainGroup) this.terrainGroup.visible = v
      this._syncTerrainCursor()
    },
    weather (v) {
      this.applyWeather(v)
    },
    terrainTool () {
      this._syncTerrainCursor()
    },
    sandPlaceItem () {
      this._syncTerrainCursor()
    }
  },
  mounted () {
    this.$nextTick(() => this.init())
  },
  beforeDestroy () {
    this.dispose()
  },
  methods: {
    /** 草面之上摆放层（空 Group，需自行 add 模型） */
    getTerrainRoot () {
      return this.terrainGroup
    },
    /** 公开方法：在沙盘中心放置一个沙具（用于新手引导） */
    placeSandPropPublic (item) {
      if (!this.sandboxRoot || !this._sandPropsGroup) return
      const center = new THREE.Vector3(0, 0, 0)
      this._placeSandProp(item, center)
    },
    /** 整个草地平台组（仅水面 + 此台，无其它装饰） */
    getGroundRoot () {
      return this.sandboxRoot
    },
    /** 设置地面 (sandboxRoot) 绕 Y 轴旋转角度（度） */
    setSandboxYaw (yawDeg) {
      if (!this.sandboxRoot) return
      const lim = this.getSandboxYawLimits()
      const clamped = Math.min(
        Math.max(Number(yawDeg) || 0, lim.min),
        lim.max
      )
      this.sandboxRoot.rotation.y = THREE.MathUtils.degToRad(clamped)
    },
    getSandboxYaw () {
      if (!this.sandboxRoot) return 0
      return THREE.MathUtils.radToDeg(this.sandboxRoot.rotation.y)
    },
    getSandboxYawLimits () {
      return { min: -180, max: 180 }
    },
    /** 在当前 yaw 上叠加一个增量（度），用于双指旋转手势 */
    setSandboxYawDelta (deltaDeg) {
      if (!this.sandboxRoot) return
      const cur = THREE.MathUtils.radToDeg(this.sandboxRoot.rotation.y)
      const lim = this.getSandboxYawLimits()
      const next = Math.min(
        Math.max(cur + (Number(deltaDeg) || 0), lim.min),
        lim.max
      )
      this.sandboxRoot.rotation.y = THREE.MathUtils.degToRad(next)
    },
    /**
     * 等比缩放地面 (sandboxRoot)。1.0 = 默认大小，>1 放大、<1 缩小。
     * 同时会把 scale 写回对应的 yaw/zoom 滑条用的内部计数：但调用方负责管理滑条值。
     */
    setSandboxScale (scale) {
      if (!this.sandboxRoot) return
      const lim = this.getSandboxScaleLimits()
      const v = Math.min(Math.max(Number(scale) || 1, lim.min), lim.max)
      this.sandboxRoot.scale.set(v, v, v)
    },
    getSandboxScale () {
      if (!this.sandboxRoot) return 1
      return this.sandboxRoot.scale.x || 1
    },
    getSandboxScaleLimits () {
      return { min: 0.5, max: 2.0 }
    },
    /**
     * 外部（双指 pinch）调用：立即取消当前的单指 pan 状态，但**不**释放 pointer capture
     * 也不伪造 pointerup 事件。
     *
     * 为什么这么做：
     * - 第一根手指落下时，playground 通过 setPointerCapture 把它的事件固定到 canvas
     * - 第二根手指落下时，IslandPlay 在 capture 阶段 stopImmediatePropagation，
     *   canvas 收不到第二个 pointerdown，pan 模式仍存在
     * - 如果我们什么都不做，pan 会一直跑，跟双指 pinch 互相冲突 → 沙盘左右闪烁
     * - 如果给 canvas 发伪造 pointerup，会触发 onPointerUp 里的 setPointerCapture
     *   release/release 路径，跟后续事件不匹配 → 状态错乱
     *
     * 正确做法：直接把 `_pointer.mode` 置 null，让 onPointerMove 静默 return。
     * 下次用户单指 down 时，pan 状态自然被重新设置。
     */
    cancelSandPan () {
      if (this._pointer && this._pointer.mode === 'pan') {
        this._pointer.mode = null
        this._pointer.id = null
        if (typeof this._syncTerrainCursor === 'function') {
          try { this._syncTerrainCursor() } catch (err) {}
        }
      }
    },

    getGroundCenter (target) {
      if (!this.sandboxRoot) {
        return target.set(0, 0.35, 0)
      }
      return target.set(
        this.sandboxRoot.position.x,
        this.sandboxRoot.position.y + this._sandCenterLocalY,
        this.sandboxRoot.position.z
      )
    },

    syncOrbitFromCamera () {
      if (!this.camera || !this._orbit) return
      const off = this._vec.subVectors(this.camera.position, this._orbit.target)
      this._orbit.spherical.setFromVector3(off)
    },

    /** 相机 orbit 变化后通知父级同步俯仰/左右/远近滑条，并刷新沙具编辑 HUD 锚点 */
    _notifyCameraOrbitChange () {
      this.$emit('camera-orbit-change')
      if (this._editingProp) {
        this._emitPropEditHud()
      }
    },

    _clampOrbitTargetToSandbox () {
      if (!this.sandboxRoot || !this._orbit || this._sandCenterLocalY == null) return
      const target = this._orbit.target
      const before = target.clone()
      const local = target.clone()
      this.sandboxRoot.worldToLocal(local)
      const hx =
        typeof this._islandHalfX === 'number' ? this._islandHalfX : ISLAND_HALF_X
      const hz =
        typeof this._islandHalfZ === 'number' ? this._islandHalfZ : ISLAND_HALF_Z
      local.x = THREE.MathUtils.clamp(local.x, -hx, hx)
      local.z = THREE.MathUtils.clamp(local.z, -hz, hz)
      local.y = this._sandCenterLocalY
      this.sandboxRoot.localToWorld(local)
      const correction = this._vec.subVectors(local, before)
      if (correction.lengthSq() > 1e-12) {
        target.copy(local)
        this.camera.position.add(correction)
      }
    },

    /** 左键拖拽：平移观看地面位置（不改变俯仰/旋转）— Cursor Write It */
    panCameraByScreenDelta (prevClientX, prevClientY, clientX, clientY) {
      if (!this.camera || !this._orbit || !this.renderer) return
      const canvas = this.renderer.domElement
      const rect = canvas.getBoundingClientRect()
      const target = this._orbit.target
      const ndcZ = target.clone().project(this.camera).z

      this._panWorldPrev.set(
        ((prevClientX - rect.left) / rect.width) * 2 - 1,
        -((prevClientY - rect.top) / rect.height) * 2 + 1,
        ndcZ
      ).unproject(this.camera)

      this._panWorldCurr.set(
        ((clientX - rect.left) / rect.width) * 2 - 1,
        -((clientY - rect.top) / rect.height) * 2 + 1,
        ndcZ
      ).unproject(this.camera)

      const delta = this._vec.subVectors(this._panWorldPrev, this._panWorldCurr)
      delta.y = 0
      if (delta.lengthSq() < 1e-12) return

      this.camera.position.add(delta)
      target.add(delta)
      this._clampOrbitTargetToSandbox()
      this.camera.up.copy(this._axisY)
      this.camera.lookAt(target)
      this.syncOrbitFromCamera()
      this._notifyCameraOrbitChange()
    },

    isTourModeActive () {
      return !!(this._tour && this._tour.active)
    },

    isTourTransitioning () {
      return !!this._tourTransition
    },

    isTourInputEnabled () {
      return this.isTourModeActive() && !this.isTourTransitioning()
    },

    _easeInOutCubic (t) {
      return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
    },

    _captureTourCameraQuaternion (outQuat) {
      const savedPos = this.camera.position.clone()
      const savedQuat = this.camera.quaternion.clone()
      this._applyTourCamera()
      outQuat.copy(this.camera.quaternion)
      this.camera.position.copy(savedPos)
      this.camera.quaternion.copy(savedQuat)
      return outQuat
    },

    _startTourCameraTransition (fromPos, fromQuat, toPos, toQuat, duration, onDone) {
      this._tourTransition = {
        elapsed: 0,
        duration: Math.max(0.2, duration),
        fromPos: fromPos.clone(),
        fromQuat: fromQuat.clone(),
        toPos: toPos.clone(),
        toQuat: toQuat.clone(),
        onDone: onDone || null
      }
    },

    _updateTourCameraTransition (dt) {
      const tr = this._tourTransition
      if (!tr || !this.camera) return
      tr.elapsed += dt
      const t = Math.min(1, tr.elapsed / tr.duration)
      const k = this._easeInOutCubic(t)
      this.camera.position.lerpVectors(tr.fromPos, tr.toPos, k)
      this.camera.quaternion.slerpQuaternions(tr.fromQuat, tr.toQuat, k)
      if (t >= 1) {
        const done = tr.onDone
        this._tourTransition = null
        if (done) done()
      }
    },

    _cancelTourCameraTransition () {
      this._tourTransition = null
    },

    _emitTourModeChange (active, transitioning) {
      this.$emit('tour-mode-change', { active: !!active, transitioning: !!transitioning })
    },

    _emitTourWaterChange (inWater) {
      if (this._tourWaterEmit === inWater) return
      this._tourWaterEmit = inWater
      this.$emit('tour-water-change', { inWater: !!inWater })
    },

    _sampleTerrainFieldBilinear (x, z, field) {
      const geo = this._sandSculptGeo
      const sizeX = this._sculptSizeX
      const sizeZ = this._sculptSizeZ
      const segX = this._sculptSegX
      const segZ = this._sculptSegZ
      if (!geo || !sizeX || !segX) {
        if (field === 'wetness') return this._sampleWetnessLocal(x, z)
        return this._sampleSculptHeightLocal(x, z)
      }
      const pos = geo.attributes.position
      const wet = geo.attributes.wetness
      if (!pos) return 0
      const parr = pos.array
      const warr = wet ? wet.array : null
      // PlaneGeometry 顶点排列：row=Z, col=X
      const u = THREE.MathUtils.clamp(((x + sizeX * 0.5) / sizeX) * segX, 0, segX)
      const v = THREE.MathUtils.clamp(((z + sizeZ * 0.5) / sizeZ) * segZ, 0, segZ)
      // 修复：先 clamp 再 floor，避免边界处索引越界导致插值错误
      const i0 = Math.min(segX - 1, Math.floor(u))
      const j0 = Math.min(segZ - 1, Math.floor(v))
      const i1 = Math.min(segX - 1, i0 + 1)
      const j1 = Math.min(segZ - 1, j0 + 1)
      const fu = u - i0
      const fv = v - j0
      const cols = segX + 1
      const read = (ci, cj) => {
        const idx = cj * cols + ci
        const i3 = idx * 3
        if (field === 'wetness') return warr ? warr[idx] : 0
        return parr[i3 + 1]
      }
      const v00 = read(i0, j0)
      const v10 = read(i1, j0)
      const v01 = read(i0, j1)
      const v11 = read(i1, j1)
      const a = v00 + (v10 - v00) * fu
      const b = v01 + (v11 - v01) * fu
      return a + (b - a) * fv
    },

    _isTourInWaterAtLocal (lx, lz) {
      const wet = this._sampleTerrainFieldBilinear(lx, lz, 'wetness')
      if (wet >= TOUR_WATER_WADE_WET) return true
      const sculptH = this._sampleTerrainFieldBilinear(lx, lz, 'height')
      return wet > 0.1 && sculptH < WATER_SURFACE_Y - 0.015
    },

    _resolveTourEyeLocalY (lx, lz, dt) {
      const tour = this._tour
      if (!tour) return 0
      const sculptH = this._sampleTerrainFieldBilinear(lx, lz, 'height')
      const inWater = this._isTourInWaterAtLocal(lx, lz)
      if (tour.wadeBlend == null) tour.wadeBlend = inWater ? 1 : 0
      const targetBlend = inWater ? 1 : 0
      const blendK = Math.min(1, (dt || 0.016) * TOUR_WATER_WADE_BLEND)
      tour.wadeBlend += (targetBlend - tour.wadeBlend) * blendK
      if (Math.abs(tour.wadeBlend - targetBlend) < 0.002) tour.wadeBlend = targetBlend
      tour.inWater = tour.wadeBlend > 0.35
      const landEye = sculptH + TOUR_EYE_HEIGHT
      const waterEye = WATER_SURFACE_Y + TOUR_WATER_EYE_OFFSET
      return landEye + (waterEye - landEye) * tour.wadeBlend
    },

    _syncTourWaterVisuals () {
      const tour = this._tour
      if (!tour) return
      const wade = tour.wadeBlend || 0
      const lookDown = THREE.MathUtils.clamp(
        (-tour.pitch - 0.08) / 0.72,
        0,
        1
      )
      if (this._poolWaterUniforms) {
        this._poolWaterUniforms.uTourWade.value = wade
        this._poolWaterUniforms.uTourLookDown.value = lookDown
      }
      if (this._sandTerrainUniforms) {
        this._sandTerrainUniforms.uTourInWater.value = tour.inWater ? 1 : 0
      }
      if (this.camera) {
        const near = tour.inWater ? 0.04 : 0.1
        if (this.camera.near !== near) {
          this.camera.near = near
          this.camera.updateProjectionMatrix()
        }
      }
      this._emitTourWaterChange(!!tour.inWater)
    },

    _prepareTourSpawn () {
      const hz =
        typeof this._islandHalfZ === 'number' ? this._islandHalfZ : ISLAND_HALF_Z
      const spawnZ = hz * (1 - TOUR_EDGE_INSET) * 0.72
      this._tour.yaw = Math.PI
      this._tour.pitch = -0.16
      this._getTourWorldPositionFromLocal(0, spawnZ, this._tour.position)
    },

    /** 进入游览：镜头俯冲至贴地视角 — Cursor Write It */
    enterTourMode () {
      if (!this.camera || !this.sandboxRoot) return false
      if (this.isTourModeActive() || this.isTourTransitioning()) return false
      this.endPropEdit(true)
      if (!this._tour) {
        this._tour = {
          active: false,
          yaw: 0,
          pitch: 0,
          wadeBlend: 0,
          inWater: false,
          position: new THREE.Vector3(),
          keys: {
            forward: false,
            back: false,
            left: false,
            right: false
          }
        }
      }
      this._tourWaterEmit = null
      if (!this._tourQuatScratch) {
        this._tourQuatScratch = new THREE.Quaternion()
      }
      this._tourSavedCam = {
        position: this.camera.position.clone(),
        quaternion: this.camera.quaternion.clone()
      }
      this._prepareTourSpawn()
      const fromPos = this.camera.position.clone()
      const fromQuat = this.camera.quaternion.clone()
      const toPos = this._tour.position.clone()
      const toQuat = this._captureTourCameraQuaternion(this._tourQuatScratch)
      this._tour.active = true
      this._reapplyAllPropTransforms()
      this._unbindTourKeys()
      if (this._pointer) this._pointer.mode = null
      this._emitTourModeChange(true, true)
      this._syncTerrainCursor()
      this._startTourCameraTransition(
        fromPos,
        fromQuat,
        toPos,
        toQuat,
        TOUR_ENTER_DURATION,
        () => {
          this.camera.position.copy(toPos)
          this.camera.quaternion.copy(toQuat)
          this._applyTourCamera()
          this._bindTourKeys()
          this._bindTourPointerLock()
          this._emitTourModeChange(true, false)
          this._syncTerrainCursor()
        }
      )
      return true
    },

    /** 退出游览：镜头拉回编辑视角 — Cursor Write It */
    exitTourMode () {
      if (!this.camera || !this.sandboxRoot) return false
      if (!this.isTourModeActive() && !this.isTourTransitioning()) return false
      this._cancelTourCameraTransition()
      this._unbindTourKeys()
      if (this._pointer) this._pointer.mode = null

      const finishExit = () => {
        this._tour.active = false
        if (this._tour) {
          this._tour.inWater = false
          this._tour.wadeBlend = 0
        }
        this._tourWaterEmit = null
        if (this._poolWaterUniforms) {
          this._poolWaterUniforms.uTourWade.value = 0
          this._poolWaterUniforms.uTourLookDown.value = 0
        }
        if (this._sandTerrainUniforms) {
          this._sandTerrainUniforms.uTourInWater.value = 0
        }
        if (this.camera && this.camera.near !== 0.1) {
          this.camera.near = 0.1
          this.camera.updateProjectionMatrix()
        }
        this._emitTourWaterChange(false)
        this._tourSavedCam = null
        this._emitTourModeChange(false, false)
        this._syncTerrainCursor()
      }

      if (!this.isTourModeActive()) {
        finishExit()
        return true
      }

      const fromPos = this.camera.position.clone()
      const fromQuat = this.camera.quaternion.clone()
      let toPos
      let toQuat
      if (this._tourSavedCam) {
        toPos = this._tourSavedCam.position
        toQuat = this._tourSavedCam.quaternion
      } else {
        this.setCameraDefaultOrbit()
        toPos = this.camera.position.clone()
        toQuat = this.camera.quaternion.clone()
        this.camera.position.copy(fromPos)
        this.camera.quaternion.copy(fromQuat)
      }
      this._emitTourModeChange(true, true)
      this._startTourCameraTransition(
        fromPos,
        fromQuat,
        toPos,
        toQuat,
        TOUR_EXIT_DURATION,
        finishExit
      )
      return true
    },

    _getTourWorldPositionFromLocal (localX, localZ, outVec, dt) {
      const hx =
        typeof this._islandHalfX === 'number' ? this._islandHalfX : ISLAND_HALF_X
      const hz =
        typeof this._islandHalfZ === 'number' ? this._islandHalfZ : ISLAND_HALF_Z
      const limX = hx * (1 - TOUR_EDGE_INSET)
      const limZ = hz * (1 - TOUR_EDGE_INSET)
      const lx = THREE.MathUtils.clamp(localX, -limX, limX)
      const lz = THREE.MathUtils.clamp(localZ, -limZ, limZ)
      const eyeLocalY = this._resolveTourEyeLocalY(lx, lz, dt)
      outVec.set(lx, this._sandCenterLocalY + eyeLocalY, lz)
      this.sandboxRoot.localToWorld(outVec)
      return outVec
    },

    _applyTourCamera () {
      if (!this.camera || !this._tour) return
      const yaw = this._tour.yaw
      const pitch = this._tour.pitch
      this.camera.position.copy(this._tour.position)
      this._dir.set(
        Math.sin(yaw) * Math.cos(pitch),
        Math.sin(pitch),
        Math.cos(yaw) * Math.cos(pitch)
      )
      this.camera.up.copy(this._axisY)
      this.camera.lookAt(this._vec.copy(this._tour.position).add(this._dir))
    },

    _bindTourKeys () {
      if (this._tourKeysBound) return
      window.addEventListener('keydown', this._onTourKeyDown)
      window.addEventListener('keyup', this._onTourKeyUp)
      this._tourKeysBound = true
    },

    _bindTourPointerLock () {
      if (this._tourPointerBound) return
      document.addEventListener('mousemove', this._onTourMouseMove)
      document.addEventListener('pointerlockchange', this._onTourPointerLockChange)
      this._tourPointerBound = true
      this._requestTourPointerLock()
    },

    _unbindTourPointerLock () {
      if (!this._tourPointerBound) return
      document.removeEventListener('mousemove', this._onTourMouseMove)
      document.removeEventListener('pointerlockchange', this._onTourPointerLockChange)
      this._tourPointerBound = false
      this._exitTourPointerLock()
    },

    _requestTourPointerLock () {
      if (!this.isTourInputEnabled()) return
      const canvas = this.renderer && this.renderer.domElement
      if (!canvas || typeof canvas.requestPointerLock !== 'function') return
      canvas.requestPointerLock().catch(() => {})
    },

    _exitTourPointerLock () {
      if (document.pointerLockElement) {
        document.exitPointerLock()
      }
    },

    _isTourPointerLocked () {
      const canvas = this.renderer && this.renderer.domElement
      return !!(canvas && document.pointerLockElement === canvas)
    },

    _onTourPointerLockChangeHandler () {
      if (this.disposed) return
      this._syncTerrainCursor()
    },

    _handleTourMouseMove (e) {
      if (!this.isTourInputEnabled() || !this._isTourPointerLocked()) return
      const dx = e.movementX || 0
      const dy = e.movementY || 0
      if (!dx && !dy) return
      this._rotateTourLook(dx, dy)
    },

    _rotateTourLook (dx, dy) {
      if (!this._tour) return
      this._tour.yaw -= dx * TOUR_LOOK_SENS
      this._tour.pitch -= dy * TOUR_LOOK_SENS
      const pitchMin = this._tour.inWater ? TOUR_PITCH_MIN_WATER : TOUR_PITCH_MIN
      this._tour.pitch = THREE.MathUtils.clamp(
        this._tour.pitch,
        pitchMin,
        TOUR_PITCH_MAX
      )
      this._applyTourCamera()
    },

    _unbindTourKeys () {
      if (!this._tourKeysBound) return
      window.removeEventListener('keydown', this._onTourKeyDown)
      window.removeEventListener('keyup', this._onTourKeyUp)
      this._tourKeysBound = false
      this._unbindTourPointerLock()
      if (this._tour && this._tour.keys) {
        this._tour.keys.forward = false
        this._tour.keys.back = false
        this._tour.keys.left = false
        this._tour.keys.right = false
      }
    },

    _handleTourKey (e, down) {
      if (!this.isTourInputEnabled() || !this._tour) return
      const code = e.code
      let handled = false
      if (code === 'KeyW' || code === 'ArrowUp') {
        this._tour.keys.forward = down
        handled = true
      } else if (code === 'KeyS' || code === 'ArrowDown') {
        this._tour.keys.back = down
        handled = true
      } else if (code === 'KeyA' || code === 'ArrowLeft') {
        this._tour.keys.left = down
        handled = true
      } else if (code === 'KeyD' || code === 'ArrowRight') {
        this._tour.keys.right = down
        handled = true
      } else if (down && code === 'Escape') {
        this.exitTourMode()
        handled = true
      }
      if (handled) e.preventDefault()
    },

    _updateTour (dt) {
      const tour = this._tour
      if (!tour || !this.sandboxRoot) return
      const keys = tour.keys
      const sinY = Math.sin(tour.yaw)
      const cosY = Math.cos(tour.yaw)
      let dx = 0
      let dz = 0
      if (keys.forward) {
        dx += sinY
        dz += cosY
      }
      if (keys.back) {
        dx -= sinY
        dz -= cosY
      }
      if (keys.left) {
        dx += cosY
        dz -= sinY
      }
      if (keys.right) {
        dx -= cosY
        dz += sinY
      }
      this._terrainHit.copy(tour.position)
      this.sandboxRoot.worldToLocal(this._terrainHit)
      if (dx !== 0 || dz !== 0) {
        const len = Math.hypot(dx, dz) || 1
        dx = (dx / len) * TOUR_MOVE_SPEED * dt
        dz = (dz / len) * TOUR_MOVE_SPEED * dt
        this._terrainHit.x += dx
        this._terrainHit.z += dz
      }
      this._getTourWorldPositionFromLocal(
        this._terrainHit.x,
        this._terrainHit.z,
        tour.position,
        dt
      )
      this._syncTourWaterVisuals()
      this._applyTourCamera()
    },

    setTourMoveKey (dir, pressed) {
      if (!this.isTourInputEnabled() || !this._tour || !this._tour.keys) return
      const map = {
        forward: 'forward',
        back: 'back',
        left: 'left',
        right: 'right'
      }
      const key = map[dir]
      if (key) this._tour.keys[key] = !!pressed
    },
    /**
     * 由调用方直接驱动游览相机 yaw/pitch（用于移动端无 Pointer Lock 的滑屏转视角）
     * dx/dy 单位为像素位移，内部按 TOUR_LOOK_SENS 系数转换为角度
     */
    setTourLook (dx, dy) {
      if (!this.isTourInputEnabled() || !this._tour) return
      const sens = TOUR_LOOK_SENS
      this._tour.yaw -= dx * sens
      this._tour.pitch -= dy * sens
      const pitchMin = this._tour.inWater ? TOUR_PITCH_MIN_WATER : TOUR_PITCH_MIN
      this._tour.pitch = THREE.MathUtils.clamp(
        this._tour.pitch,
        pitchMin,
        TOUR_PITCH_MAX
      )
      this._applyTourCamera()
    },

    /** 重置为正面 45° 俯视（+Z 侧看向沙盘中心）— Cursor Write It */
    setCameraDefaultOrbit () {
      if (!this.camera || !this.sandboxRoot || !this._orbit) return
      const target = this.getGroundCenter(this._orbit.target)
      const r = THREE.MathUtils.clamp(
        this._cameraDefaultRadius,
        this._orbitMinRadius,
        this._orbitMaxRadius
      )
      const horiz = r * Math.cos(CAMERA_DEFAULT_ELEVATION)
      const yOff = r * Math.sin(CAMERA_DEFAULT_ELEVATION)
      const offset = this._vec.set(
        horiz * Math.sin(CAMERA_DEFAULT_AZIMUTH),
        yOff,
        horiz * Math.cos(CAMERA_DEFAULT_AZIMUTH)
      )
      this.camera.position.copy(target).add(offset)
      this.camera.up.copy(this._axisY)
      this.camera.lookAt(target)
      this._orbit.spherical.setFromVector3(
        this._vec.subVectors(this.camera.position, target)
      )
      this._notifyCameraOrbitChange()
    },

    /** 以沙面中心为枢轴旋转相机（弧度）— Cursor Write It */
    orbitCameraByAngles (yawRad, pitchRad) {
      if (!this.camera || !this.sandboxRoot || !this._orbit) return
      const target = this._orbit.target

      const offset = this._vec.subVectors(this.camera.position, target)
      let radius = offset.length()
      if (radius < this._orbitMinRadius) {
        offset.normalize().multiplyScalar(this._orbitMinRadius)
        radius = this._orbitMinRadius
      }
      if (radius > this._orbitMaxRadius) {
        offset.normalize().multiplyScalar(this._orbitMaxRadius)
        radius = this._orbitMaxRadius
      }

      if (yawRad) {
        this._qYaw.setFromAxisAngle(this._axisY, -yawRad)
        offset.applyQuaternion(this._qYaw)
      }

      if (pitchRad) {
        this._dir.copy(offset).normalize()
        this._pitchAxis.crossVectors(this._dir, this._axisY)
        if (this._pitchAxis.lengthSq() < 1e-10) {
          this._pitchAxis.set(1, 0, 0)
        } else {
          this._pitchAxis.normalize()
        }
        this._qPitch.setFromAxisAngle(this._pitchAxis, -pitchRad)
        offset.applyQuaternion(this._qPitch)
      }

      offset.multiplyScalar(radius / offset.length())

      const camY = target.y + offset.y
      const minY = target.y + CAMERA_MIN_ABOVE_TARGET
      if (camY < minY) {
        offset.y += minY - camY
        offset.multiplyScalar(radius / offset.length())
      }

      this.camera.position.copy(target).add(offset)
      this.camera.up.copy(this._axisY)
      this.camera.lookAt(target)
      this.syncOrbitFromCamera()
      this._notifyCameraOrbitChange()
    },

    /** 指针拖拽：像素位移转弧度 — Cursor Write It */
    rotateCameraOrbit (dx, dy) {
      this.orbitCameraByAngles(dx * YAW_SENS, dy * PITCH_SENS)
    },

    /** 缩放：沿视线方向推拉相机距离 — Cursor Write It */
    zoomCameraOrbit (deltaRadius) {
      if (!this.camera || !this.sandboxRoot || !this._orbit || !deltaRadius) return
      const target = this._orbit.target
      const offset = this._vec.subVectors(this.camera.position, target)
      let radius = offset.length()
      if (radius < 1e-6) return
      radius = THREE.MathUtils.clamp(
        radius - deltaRadius,
        this._orbitMinRadius,
        this._orbitMaxRadius
      )
      offset.normalize().multiplyScalar(radius)
      this.camera.position.copy(target).add(offset)
      this.camera.up.copy(this._axisY)
      this.camera.lookAt(target)
      this.syncOrbitFromCamera()
      this._notifyCameraOrbitChange()
    },

    /** 设置相机与沙盘中心距离 — Cursor Write It */
    setCameraOrbitRadius (radius, notify = true) {
      if (!this.camera || !this.sandboxRoot || !this._orbit) return
      const target = this._orbit.target
      const offset = this._vec.subVectors(this.camera.position, target)
      if (offset.lengthSq() < 1e-10) {
        offset.set(0, 1, 1).normalize()
      } else {
        offset.normalize()
      }
      const r = THREE.MathUtils.clamp(radius, this._orbitMinRadius, this._orbitMaxRadius)
      offset.multiplyScalar(r)
      this.camera.position.copy(target).add(offset)
      this.camera.up.copy(this._axisY)
      this.camera.lookAt(target)
      this.syncOrbitFromCamera()
      if (notify) this._notifyCameraOrbitChange()
    },

    /** 当前相机距离 — Cursor Write It */
    getCameraOrbitRadius () {
      if (!this.camera || !this.sandboxRoot || !this._orbit) return null
      return this._vec.subVectors(this.camera.position, this._orbit.target).length()
    },

    /** 相机缩放范围 — Cursor Write It */
    getCameraOrbitLimits () {
      return { min: this._orbitMinRadius, max: this._orbitMaxRadius }
    },

    /** 手机端：拉近默认视角，并将最远缩放限制为原 PC 默认视距 */
    useMobileOrbitProfile () {
      this._orbitMaxRadius = MOBILE_ORBIT_MAX_RADIUS
      this._cameraDefaultRadius = MOBILE_CAMERA_DEFAULT_RADIUS
      this.setCameraOrbitRadius(MOBILE_CAMERA_DEFAULT_RADIUS)
    },

    _normalizeYawDeg (yawDeg) {
      let yaw = yawDeg
      while (yaw > 180) yaw -= 360
      while (yaw < -180) yaw += 360
      return yaw
    },

    /** 当前俯仰/左右角度（度）：pitch=仰俯角，yaw=左右 — Cursor Write It */
    getCameraOrbitAngles () {
      if (!this.camera || !this.sandboxRoot || !this._orbit) return null
      const target = this._orbit.target
      const offset = this._vec.subVectors(this.camera.position, target)
      if (offset.lengthSq() < 1e-10) return null
      this._orbit.spherical.setFromVector3(offset)
      return {
        yawDeg: this._normalizeYawDeg(
          -THREE.MathUtils.radToDeg(this._orbit.spherical.theta)
        ),
        pitchDeg: THREE.MathUtils.radToDeg(
          Math.PI / 2 - this._orbit.spherical.phi
        ),
        radius: offset.length()
      }
    },

    getCameraOrbitYawLimits () {
      return { min: -180, max: 180 }
    },

    getCameraOrbitPitchLimits () {
      return { min: 8, max: 82 }
    },

    _applyOrbitFromAngles (yawDeg, pitchDeg, radius, notify = true) {
      if (!this.camera || !this.sandboxRoot || !this._orbit) return
      const target = this._orbit.target
      const pitchLim = this.getCameraOrbitPitchLimits()
      const pitchClamped = THREE.MathUtils.clamp(
        pitchDeg,
        pitchLim.min,
        pitchLim.max
      )
      const yawRad = THREE.MathUtils.degToRad(this._normalizeYawDeg(yawDeg))
      const phi = THREE.MathUtils.degToRad(90 - pitchClamped)
      let r = radius
      if (r == null || !isFinite(r)) {
        r = this.getCameraOrbitRadius()
      }
      r = THREE.MathUtils.clamp(
        r || this._cameraDefaultRadius,
        this._orbitMinRadius,
        this._orbitMaxRadius
      )

      this._orbit.spherical.set(r, phi, yawRad)
      const offset = this._vec.setFromSpherical(this._orbit.spherical)

      const camY = target.y + offset.y
      const minY = target.y + CAMERA_MIN_ABOVE_TARGET
      if (camY < minY) {
        offset.y += minY - camY
        const len = offset.length()
        if (len > 1e-6) {
          offset.multiplyScalar(r / len)
        }
      }

      this.camera.position.copy(target).add(offset)
      this.camera.up.copy(this._axisY)
      this.camera.lookAt(target)
      this.syncOrbitFromCamera()
      if (notify) this._notifyCameraOrbitChange()
    },

    setCameraOrbitYaw (yawDeg) {
      const angles = this.getCameraOrbitAngles()
      if (!angles) return
      this._applyOrbitFromAngles(-yawDeg, angles.pitchDeg, angles.radius, false)
    },

    setCameraOrbitPitch (pitchDeg) {
      const angles = this.getCameraOrbitAngles()
      if (!angles) return
      // 与 setCameraOrbitYaw 一致：内部 theta = -滑条 yaw，避免改俯仰时左右角翻转抖动
      this._applyOrbitFromAngles(-angles.yawDeg, pitchDeg, angles.radius, false)
    },

    /**
     * 仅草地浮台：顶 + 浅蓝半透明侧壁 + 多层草点片（不同尺度纹理叠在顶面上方），无木框等额外几何
     */
    buildGrassPlatform (root) {
      const islandW = ISLAND_WIDTH
      const islandD = ISLAND_DEPTH
      const islandH = 0.38
      const widthDepthRatio = islandW / islandD

      const topGreen = new THREE.MeshBasicMaterial({
        color: GRASS_TOP_BASE,
        fog: false
      })
      this._grassTopMaterial = topGreen
      const sideIce = new THREE.MeshLambertMaterial({
        color: 0xb3e5fc,
        transparent: true,
        opacity: 0.78,
        emissive: 0x4fc3f7,
        emissiveIntensity: 0.2,
        fog: false
      })
      const bottomMat = new THREE.MeshLambertMaterial({
        color: 0x2e7d32,
        fog: false
      })
      const materials = [
        sideIce,
        sideIce,
        topGreen,
        bottomMat,
        sideIce,
        sideIce
      ]
      const island = new THREE.Mesh(
        new THREE.BoxGeometry(islandW, islandH, islandD),
        materials
      )
      island.position.y = 0.15
      island.castShadow = false
      island.receiveShadow = false
      root.add(island)

      this._islandHalfX = islandW * 0.5
      this._islandHalfZ = islandD * 0.5

      const topY = island.position.y + islandH / 2
      const planeW = islandW * GRASS_TOP_INSET
      const planeD = islandD * GRASS_TOP_INSET
      const dotLayers = [
        {
          seed: 31041,
          repeat: [11 * widthDepthRatio, 11],
          y: 0.0011,
          dotCount: 540,
          radiusMin: 0.32,
          radiusMax: 1.05
        },
        {
          seed: 92312,
          repeat: [5.5 * widthDepthRatio, 5.5],
          y: 0.00235,
          dotCount: 175,
          radiusMin: 0.85,
          radiusMax: 2.05
        },
        {
          seed: 44102,
          repeat: [2.35 * widthDepthRatio, 2.35],
          y: 0.00365,
          dotCount: 38,
          radiusMin: 1.85,
          radiusMax: 4.2
        }
      ]
      dotLayers.forEach((cfg, idx) => {
        const map = createGrassDotTexture({
          canvasSize: 256,
          seed: cfg.seed,
          dotCount: cfg.dotCount,
          radiusMin: cfg.radiusMin,
          radiusMax: cfg.radiusMax
        })
        map.repeat.set(cfg.repeat[0], cfg.repeat[1])
        const mat = new THREE.MeshBasicMaterial({
          map,
          transparent: true,
          opacity: 1,
          depthWrite: false,
          polygonOffset: true,
          polygonOffsetFactor: -1,
          polygonOffsetUnits: -2 - idx,
          fog: false
        })
        const speckGeo = new THREE.PlaneGeometry(planeW, planeD, 1, 1)
        const speck = new THREE.Mesh(speckGeo, mat)
        speck.rotation.x = -Math.PI / 2
        speck.position.y = topY + cfg.y
        speck.renderOrder = 2 + idx
        if (!this._grassSpeckMeshes) this._grassSpeckMeshes = []
        this._grassSpeckMeshes.push(speck)
        root.add(speck)
      })

      this._sandCenterLocalY = island.position.y + islandH / 2
      this._terrainLocalY = this._sandCenterLocalY + 0.006
    },

    init () {
      const host = this.$refs.host
      if (!host) return

      this._vec = new THREE.Vector3()
      this._axisY = new THREE.Vector3(0, 1, 0)
      this._dir = new THREE.Vector3()
      this._pitchAxis = new THREE.Vector3()
      this._qYaw = new THREE.Quaternion()
      this._qPitch = new THREE.Quaternion()
      this._orbit = {
        target: new THREE.Vector3(),
        spherical: new THREE.Spherical()
      }
      this._tour = {
        active: false,
        yaw: 0,
        pitch: 0,
        position: new THREE.Vector3(),
        keys: {
          forward: false,
          back: false,
          left: false,
          right: false
        }
      }
      this._tourSavedCam = null
      this._tourTransition = null
      this._tourQuatScratch = new THREE.Quaternion()
      this._tourKeysBound = false
      this._tourPointerBound = false
      this._onTourMouseMove = (e) => this._handleTourMouseMove(e)
      this._onTourPointerLockChange = () => this._onTourPointerLockChangeHandler()
      this._onTourKeyDown = (e) => this._handleTourKey(e, true)
      this._onTourKeyUp = (e) => this._handleTourKey(e, false)

      this._ndc = new THREE.Vector2()
      this._raycaster = new THREE.Raycaster()
      this._terrainHit = new THREE.Vector3()
      this._sandSurfWorldScratch = new THREE.Vector3()
      this._terrainStroke = new THREE.Vector3()
      this._terrainStrokePrev = new THREE.Vector3()
      this._terrainStrokeLerp = new THREE.Vector3()
      this._sculptCenterLocal = new THREE.Vector3()
      this._propWorldScratch = new THREE.Vector3()
      this._panWorldPrev = new THREE.Vector3()
      this._panWorldCurr = new THREE.Vector3()
      this._sculptMovedThisFrame = false
      this._editingProp = null
      this._editSnapshot = null

      this._orbitMinRadius = ORBIT_MIN_RADIUS
      this._orbitMaxRadius = ORBIT_MAX_RADIUS
      this._cameraDefaultRadius = CAMERA_DEFAULT_RADIUS

      const w = host.clientWidth || 800
      const h = host.clientHeight || 600

      this.scene = new THREE.Scene()
      this.scene.background = new THREE.Color(0x90caf9)
      this.scene.fog = new THREE.Fog(0x90caf9, 28, 85)

      this.camera = new THREE.PerspectiveCamera(40, w / h, 0.1, 200)

      this.renderer = new THREE.WebGLRenderer({ antialias: true })
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2))
      this.renderer.setSize(w, h)
      this.renderer.shadowMap.enabled = false
      if (THREE.sRGBEncoding !== undefined) {
        this.renderer.outputEncoding = THREE.sRGBEncoding
      }
      host.appendChild(this.renderer.domElement)

      const hemi = new THREE.HemisphereLight(0xe3f7e8, 0x1a4d2a, 0.95)
      this.scene.add(hemi)
      const sun = new THREE.DirectionalLight(0xffffff, 1.02)
      sun.position.set(6, 16, 8)
      sun.castShadow = false
      this.scene.add(sun)

      this.hemiLight = hemi
      this.dirLight = sun

      const weatherRoot = new THREE.Group()
      weatherRoot.name = 'WeatherFX'
      this.scene.add(weatherRoot)
      this.rainPoints = this._makeRainPoints()
      this.snowPoints = this._makeSnowPoints()
      weatherRoot.add(this.rainPoints)
      weatherRoot.add(this.snowPoints)
      this.rainPoints.visible = false
      this.snowPoints.visible = false

      this.lastTickMs = performance.now()
      this.applyWeather(this.weather)

      const waterGeo = new THREE.PlaneGeometry(120, 120, 80, 80)
      waterGeo.rotateX(-Math.PI / 2)
      this.waterUniforms = {
        uTime: { value: 0 },
        uDeep: { value: new THREE.Color(0x0288d1) },
        uColor: { value: new THREE.Color(0x29b6f6) },
        uHighlight: { value: new THREE.Color(0xb2ebf2) }
      }
      const waterMat = new THREE.ShaderMaterial({
        uniforms: this.waterUniforms,
        vertexShader: WATER_VS,
        fragmentShader: WATER_FS,
        fog: false
      })
      const water = new THREE.Mesh(waterGeo, waterMat)
      water.position.y = -0.42
      this.scene.add(water)

      this.sandboxRoot = new THREE.Group()
      this.sandboxRoot.name = '沙盘'
      this.sandboxRoot.position.set(0, 0.18, 0)
      this.scene.add(this.sandboxRoot)

      this.buildGrassPlatform(this.sandboxRoot)

      this._sandPropsGroup = new THREE.Group()
      this._sandPropsGroup.name = 'SandProps'
      this.sandboxRoot.add(this._sandPropsGroup)
      this._sandPropPlaceOrder = 0

      const propFill = new THREE.PointLight(0xfff5e6, 0.55, 8)
      propFill.position.set(0, 1.2, 0)
      this._propFillLight = propFill
      this.sandboxRoot.add(propFill)

      this.terrainGroup = new THREE.Group()
      this.terrainGroup.name = 'TerrainRoot'
      this.terrainGroup.position.set(0, this._terrainLocalY, 0)
      this.terrainGroup.visible = this.terrainVisible
      this.sandboxRoot.add(this.terrainGroup)

      this._buildSandSculptMesh()
      this.applyWeather(this.weather)

      this.setCameraDefaultOrbit()

      this._pointer = {
        mode: null,
        lastX: 0,
        lastY: 0,
        id: null,
        terrainLastOk: false
      }
      this._onPointerDown = (e) => this.onPointerDown(e)
      this._onPointerMove = (e) => this.onPointerMove(e)
      this._onPointerUp = (e) => this.onPointerUp(e)
      this._onContextMenu = (e) => e.preventDefault()

      const canvas = this.renderer.domElement
      this._applyCanvasCursor(canvas, 'default')
      canvas.addEventListener('pointerdown', this._onPointerDown)
      canvas.addEventListener('pointermove', this._onPointerMove)
      canvas.addEventListener('pointerup', this._onPointerUp)
      canvas.addEventListener('pointercancel', this._onPointerUp)
      canvas.addEventListener('lostpointercapture', this._onPointerUp)
      canvas.addEventListener('contextmenu', this._onContextMenu)

      this.onResize = () => this.handleResize()
      window.addEventListener('resize', this.onResize)
      if (typeof ResizeObserver !== 'undefined') {
        this.ro = new ResizeObserver(() => this.handleResize())
        this.ro.observe(host)
      }

      this.tick()
      this._syncTerrainCursor()
      preloadSandModels(SAND_MODEL_PRELOAD).catch(() => {})
    },

    _buildSandSculptMesh () {
      if (
        !this.terrainGroup ||
        typeof this._islandHalfX !== 'number' ||
        typeof this._islandHalfZ !== 'number'
      ) {
        return
      }
      const sizeX = this._islandHalfX * 2 * SCULPT_PLANE_INSET
      const sizeZ = this._islandHalfZ * 2 * SCULPT_PLANE_INSET
      const segZ = Math.max(
        8,
        Math.round(SCULPT_GRID_SEG * (ISLAND_DEPTH / ISLAND_WIDTH))
      )
      const geo = new THREE.PlaneGeometry(sizeX, sizeZ, SCULPT_GRID_SEG, segZ)
      geo.applyMatrix4(new THREE.Matrix4().makeRotationX(-Math.PI / 2))
      const posAttr = geo.attributes.position
      const n = posAttr.count
      const colors = new Float32Array(n * 3)
      for (let i = 0; i < n * 3; i++) colors[i] = 1
      geo.setAttribute('color', new THREE.BufferAttribute(colors, 3))
      const wetness = new Float32Array(n)
      geo.setAttribute('wetness', new THREE.BufferAttribute(wetness, 1))
      const matType = new Float32Array(n)
      geo.setAttribute('matType', new THREE.BufferAttribute(matType, 1))

      this._sandTerrainUniforms = {
        uTime: { value: 0 },
        uAmbient: { value: new THREE.Color(0xe8ebe8) },
        uSunColor: { value: new THREE.Color(0xffffff) },
        uLightDir: { value: new THREE.Vector3(0.48, 0.82, 0.38).normalize() },
        uMatGrass: { value: new THREE.Color(0x38d96a) },
        uMatSand: { value: new THREE.Color(0xc9a66b) },
        uMatSnow: { value: new THREE.Color(0xf5f5f5) },
        uMatBrick: { value: new THREE.Color(0x6d4c41) },
        uMatRock: { value: new THREE.Color(0x9e9e9e) },
        uWaterBody: { value: new THREE.Color(0x6ec1ff) },
        uWaterGlow: { value: new THREE.Color(0xa8d4ff) },
        uTourInWater: { value: 0 },
        uLightMul: { value: 1 }
      }
      const mat = new THREE.ShaderMaterial({
        uniforms: this._sandTerrainUniforms,
        vertexShader: SAND_TERRAIN_VS,
        fragmentShader: SAND_TERRAIN_FS,
        fog: false,
        transparent: true,
        depthWrite: true,
        depthTest: true,
        polygonOffset: true,
        polygonOffsetFactor: -2,
        polygonOffsetUnits: -2,
        side: THREE.DoubleSide
      })
      const mesh = new THREE.Mesh(geo, mat)
      mesh.name = 'SandSculpt'
      mesh.renderOrder = 20
      mesh.castShadow = false
      mesh.receiveShadow = false
      this.terrainGroup.add(mesh)
      this._sandSculptMesh = mesh
      this._sandSculptGeo = geo
      this._sculptSizeX = sizeX
      this._sculptSizeZ = sizeZ
      this._sculptSegX = SCULPT_GRID_SEG
      this._sculptSegZ = segZ
      this._buildSculptVertGrid()
      this._buildWaterSurfaceMesh()
      this._buildSandSculptSideMesh()
    },

    /**
     * 构建顶点空间网格索引：把顶点按 XZ 分桶，笔刷时只遍历命中桶
     * 桶尺寸取笔刷直径的 ~1.5 倍，保证命中桶数 ≤ ~9
     */
    _buildSculptVertGrid () {
      const geo = this._sandSculptGeo
      const posAttr = geo.attributes.position
      const marr = geo.attributes.matType ? geo.attributes.matType.array : null
      if (!posAttr || !marr) return
      const cell = SCULPT_BRUSH_R * MATERIAL_BRUSH_R_MUL * 3
      const halfX = this._sculptSizeX / 2
      const halfZ = this._sculptSizeZ / 2
      const cols = Math.max(1, Math.ceil((this._sculptSizeX + cell * 2) / cell))
      const rows = Math.max(1, Math.ceil((this._sculptSizeZ + cell * 2) / cell))
      const buckets = new Array(cols * rows)
      for (let i = 0; i < buckets.length; i++) buckets[i] = null
      const arr = posAttr.array
      const n = posAttr.count
      for (let i = 0; i < n; i++) {
        const i3 = i * 3
        const x = arr[i3]
        const z = arr[i3 + 2]
        // 与笔刷查询一致的映射方式：顶点到桶的映射（+cell 确保顶点归属正确桶）
        const cx = Math.max(0, Math.min(cols - 1, Math.floor((x + halfX + cell) / cell)))
        const cz = Math.max(0, Math.min(rows - 1, Math.floor((z + halfZ + cell) / cell)))
        const key = cz * cols + cx
        let list = buckets[key]
        if (!list) {
          list = []
          buckets[key] = list
        }
        list.push(i)
      }
      this._vertGrid = { cell, halfX, halfZ, cols, rows, buckets }
    },

    /** 堆沙围合：按格子挤出侧壁+底面，侧面看是实心块体 */
    _buildSandSculptSideMesh () {
      if (!this.terrainGroup) return
      if (this._sandSculptSideMesh) {
        this.terrainGroup.remove(this._sandSculptSideMesh)
        if (this._sandSculptSideMesh.geometry) this._sandSculptSideMesh.geometry.dispose()
        if (this._sandSculptSideMesh.material) this._sandSculptSideMesh.material.dispose()
        this._sandSculptSideMesh = null
      }
      const mat = new THREE.MeshLambertMaterial({
        color: SCULPT_SIDE_COLOR,
        fog: false,
        side: THREE.FrontSide,
        polygonOffset: true,
        polygonOffsetFactor: 1,
        polygonOffsetUnits: 1
      })
      const mesh = new THREE.Mesh(new THREE.BufferGeometry(), mat)
      mesh.name = 'SandSculptSides'
      mesh.renderOrder = 19
      mesh.castShadow = false
      mesh.receiveShadow = false
      this.terrainGroup.add(mesh)
      this._sandSculptSideMesh = mesh
      this._rebuildSandSculptSideMesh()
    },

    _rebuildSandSculptSideMesh () {
      const sideMesh = this._sandSculptSideMesh
      const geo = this._sandSculptGeo
      if (!sideMesh || !geo) return
      const srcPos = geo.attributes.position
      if (!srcPos) return
      const arr = srcPos.array
      const wetAttr = geo.attributes.wetness
      const warr = wetAttr ? wetAttr.array : null
      const segX = this._sculptSegX != null ? this._sculptSegX : SCULPT_GRID_SEG
      const segZ = this._sculptSegZ != null
        ? this._sculptSegZ
        : Math.max(8, Math.round(SCULPT_GRID_SEG * (ISLAND_DEPTH / ISLAND_WIDTH)))
      const cols = segX + 1
      const positions = []
      const indices = []

      // 直接索引访问，避免 read() 创建对象
      const cellMax = (ix, iz) => {
        if (ix < 0 || iz < 0 || ix >= segX || iz >= segZ) return 0
        const i00 = (iz * cols + ix) * 3
        const i10 = (iz * cols + (ix + 1)) * 3
        const i01 = ((iz + 1) * cols + ix) * 3
        const i11 = ((iz + 1) * cols + (ix + 1)) * 3
        const h00 = arr[i00 + 1]
        const h10 = arr[i10 + 1]
        const h01 = arr[i01 + 1]
        const h11 = arr[i11 + 1]
        return Math.max(h00, h10, h01, h11)
      }

      const cellMaxWet = (ix, iz) => {
        if (!warr || ix < 0 || iz < 0 || ix >= segX || iz >= segZ) return 0
        const i00 = iz * cols + ix
        const i10 = iz * cols + (ix + 1)
        const i01 = (iz + 1) * cols + ix
        const i11 = (iz + 1) * cols + (ix + 1)
        return Math.max(warr[i00], warr[i10], warr[i01], warr[i11])
      }

      const edgeHighEnough = (a, b) => Math.max(a, b) >= SCULPT_SIDE_EDGE_MIN_H

      // 行/列级 early skip：预扫描每行每列最大高度，全为 0 直接跳过
      const rowMax = new Float32Array(segZ)
      const colMax = new Float32Array(segX)
      const cellMaxArr = new Float32Array(segX * segZ)
      const cellWetArr = new Float32Array(segX * segZ)
      for (let iz = 0; iz < segZ; iz++) {
        for (let ix = 0; ix < segX; ix++) {
          const m = cellMax(ix, iz)
          const w = cellMaxWet(ix, iz)
          cellMaxArr[iz * segX + ix] = m
          cellWetArr[iz * segX + ix] = w
          if (m > rowMax[iz]) rowMax[iz] = m
          if (m > colMax[ix]) colMax[ix] = m
        }
      }

      for (let iz = 0; iz < segZ; iz++) {
        // 行级快速跳过
        if (rowMax[iz] < SCULPT_SIDE_MIN_H) continue
        for (let ix = 0; ix < segX; ix++) {
          // 列级快速跳过 + cellActive 复用预扫描值
          if (colMax[ix] < SCULPT_SIDE_MIN_H) continue
          const h = cellMaxArr[iz * segX + ix]
          if (h < SCULPT_SIDE_MIN_H) continue
          const w = cellWetArr[iz * segX + ix]
          if (w > 0.14 && h < 0.035) continue

          // 直接读 arr 避免 read() 创建对象
          const i00 = (iz * cols + ix) * 3
          const i10 = (iz * cols + (ix + 1)) * 3
          const i01 = ((iz + 1) * cols + ix) * 3
          const i11 = ((iz + 1) * cols + (ix + 1)) * 3
          const v00y = arr[i00 + 1]
          const v10y = arr[i10 + 1]
          const v01y = arr[i01 + 1]
          const v11y = arr[i11 + 1]
          const v00x = arr[i00]
          const v00z = arr[i00 + 2]
          const v10x = arr[i10]
          const v10z = arr[i10 + 2]
          const v01x = arr[i01]
          const v01z = arr[i01 + 2]
          const v11x = arr[i11]
          const v11z = arr[i11 + 2]

          // 仅朝外的侧壁（不加底面，避免俯视出现浅色方格）
          const nb = (iz === 0 || cellMaxArr[(iz - 1) * segX + ix] < SCULPT_SIDE_MIN_H || (cellWetArr[(iz - 1) * segX + ix] > 0.14 && cellMaxArr[(iz - 1) * segX + ix] < 0.035))
          if (nb && edgeHighEnough(v00y, v10y)) {
            positions.push(v00x, v00y, v00z, v10x, v10y, v10z, v10x, 0, v10z, v00x, 0, v00z)
            const base = (positions.length / 3) - 4
            indices.push(base, base + 1, base + 2, base, base + 2, base + 3)
          }
          const nb2 = (iz === segZ - 1 || cellMaxArr[(iz + 1) * segX + ix] < SCULPT_SIDE_MIN_H || (cellWetArr[(iz + 1) * segX + ix] > 0.14 && cellMaxArr[(iz + 1) * segX + ix] < 0.035))
          if (nb2 && edgeHighEnough(v01y, v11y)) {
            positions.push(v11x, v11y, v11z, v01x, v01y, v01z, v01x, 0, v01z, v11x, 0, v11z)
            const base = (positions.length / 3) - 4
            indices.push(base, base + 1, base + 2, base, base + 2, base + 3)
          }
          const nb3 = (ix === 0 || cellMaxArr[iz * segX + (ix - 1)] < SCULPT_SIDE_MIN_H || (cellWetArr[iz * segX + (ix - 1)] > 0.14 && cellMaxArr[iz * segX + (ix - 1)] < 0.035))
          if (nb3 && edgeHighEnough(v00y, v01y)) {
            positions.push(v01x, v01y, v01z, v00x, v00y, v00z, v00x, 0, v00z, v01x, 0, v01z)
            const base = (positions.length / 3) - 4
            indices.push(base, base + 1, base + 2, base, base + 2, base + 3)
          }
          const nb4 = (ix === segX - 1 || cellMaxArr[iz * segX + (ix + 1)] < SCULPT_SIDE_MIN_H || (cellWetArr[iz * segX + (ix + 1)] > 0.14 && cellMaxArr[iz * segX + (ix + 1)] < 0.035))
          if (nb4 && edgeHighEnough(v10y, v11y)) {
            positions.push(v10x, v10y, v10z, v11x, v11y, v11z, v11x, 0, v11z, v10x, 0, v10z)
            const base = (positions.length / 3) - 4
            indices.push(base, base + 1, base + 2, base, base + 2, base + 3)
          }
        }
      }

      const oldGeo = sideMesh.geometry
      if (!positions.length) {
        sideMesh.visible = false
        this._sandSculptSideHasGeo = false
        if (oldGeo) oldGeo.dispose()
        sideMesh.geometry = new THREE.BufferGeometry()
        return
      }

      sideMesh.visible = positions.length > 0 && this._shouldShowSandSculptSides()
      this._sandSculptSideHasGeo = positions.length > 0
      const nextGeo = new THREE.BufferGeometry()
      nextGeo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3))
      nextGeo.setIndex(indices)
      nextGeo.computeVertexNormals()
      sideMesh.geometry = nextGeo
      if (oldGeo) oldGeo.dispose()
    },

    _shouldShowSandSculptSides () {
      const angles = this.getCameraOrbitAngles()
      if (!angles) return true
      return angles.pitchDeg <= SCULPT_SIDE_PITCH_SHOW_MAX
    },

    _syncSandSculptSideVisibility () {
      const sideMesh = this._sandSculptSideMesh
      if (!sideMesh || !this._sandSculptSideHasGeo) return
      const show = this._shouldShowSandSculptSides()
      if (sideMesh.visible !== show) sideMesh.visible = show
    },

    _buildWaterSurfaceMesh () {
      if (!this._sandSculptGeo || !this.terrainGroup) return
      if (this._waterSurfaceMesh) {
        this.terrainGroup.remove(this._waterSurfaceMesh)
        if (this._waterSurfaceMesh.geometry) this._waterSurfaceMesh.geometry.dispose()
        if (this._waterSurfaceMesh.material) this._waterSurfaceMesh.material.dispose()
        this._waterSurfaceMesh = null
      }
      const srcGeo = this._sandSculptGeo
      const pos = srcGeo.attributes.position
      const wetness = srcGeo.attributes.wetness
      if (!pos || !wetness) return

      const geo = new THREE.BufferGeometry()
      geo.setAttribute(
        'position',
        new THREE.BufferAttribute(new Float32Array(pos.array), 3)
      )
      geo.setAttribute(
        'wetness',
        new THREE.BufferAttribute(new Float32Array(wetness.array), 1)
      )
      if (srcGeo.index) geo.setIndex(srcGeo.index.clone())
      this._syncWaterSurfaceMesh(geo, true)

      this._poolWaterUniforms = {
        uTime: { value: 0 },
        uWaterBody: { value: new THREE.Color(0x6ec1ff) },
        uWaterGlow: { value: new THREE.Color(0xa8d4ff) },
        uTourWade: { value: 0 },
        uTourLookDown: { value: 0 },
        uLightMul: { value: 1 }
      }
      const mat = new THREE.ShaderMaterial({
        uniforms: this._poolWaterUniforms,
        vertexShader: POOL_WATER_SURFACE_VS,
        fragmentShader: POOL_WATER_SURFACE_FS,
        transparent: true,
        depthWrite: false,
        depthTest: true,
        polygonOffset: true,
        polygonOffsetFactor: -1,
        polygonOffsetUnits: -1,
        side: THREE.FrontSide,
        fog: false
      })
      const mesh = new THREE.Mesh(geo, mat)
      mesh.name = 'PoolWaterSurface'
      mesh.renderOrder = 24
      this.terrainGroup.add(mesh)
      this._waterSurfaceMesh = mesh
    },

    _syncWaterSurfaceMesh (targetGeo, forceFullSync) {
      // 强制完整同步仅用于初始化（设置水面 Y）
      if (forceFullSync && targetGeo) {
        const geo = this._sandSculptGeo
        const srcPos = geo.attributes.position
        const srcWet = geo.attributes.wetness
        const dstAttr = targetGeo.attributes.position
        const dstWetAttr = targetGeo.attributes.wetness
        if (dstAttr && srcPos) {
          const src = srcPos.array
          const dst = dstAttr.array
          const surfaceY = WATER_SURFACE_Y
          for (let i = 0, n = dstAttr.count; i < n; i++) {
            const i3 = i * 3
            dst[i3] = src[i3]
            dst[i3 + 1] = surfaceY
            dst[i3 + 2] = src[i3 + 2]
          }
          dstAttr.needsUpdate = true
          targetGeo.computeVertexNormals()
        }
        if (dstWetAttr && srcWet) {
          const srcWetArr = srcWet.array
          const dstWetArr = dstWetAttr.array
          const src = srcPos.array
          for (let i = 0, n = dstWetArr.length; i < n; i++) {
            const terrainH = src[i * 3 + 1]
            dstWetArr[i] = terrainH > WATER_MAX_TERRAIN_Y ? 0 : srcWetArr[i]
          }
          dstWetAttr.needsUpdate = true
        }
        return
      }
      // 节流：每帧只在 tick 末尾 _flushWaterSync 一次 wetness 同步
      this._waterSyncPending = true
    },
    _flushWaterSync () {
      if (!this._waterSyncPending) return
      this._waterSyncPending = false
      const targetGeo = this._waterSurfaceMesh && this._waterSurfaceMesh.geometry
      const geo = this._sandSculptGeo
      if (!targetGeo || !geo) return
      // sculpt mesh 的 XZ 是静态的（plane），只有 Y 变化；水面 mesh XZ 初始化一次后不变
      // 每帧只需同步 wetness（决定水面是否在该顶点可见）
      const srcPos = geo.attributes.position
      const srcWet = geo.attributes.wetness
      const dstWetAttr = targetGeo.attributes.wetness
      if (!dstWetAttr || !srcWet) return
      const srcWetArr = srcWet.array
      const dstWetArr = dstWetAttr.array
      const src = srcPos.array
      for (let i = 0, n = dstWetArr.length; i < n; i++) {
        const terrainH = src[i * 3 + 1]
        dstWetArr[i] = terrainH > WATER_MAX_TERRAIN_Y ? 0 : srcWetArr[i]
      }
      dstWetAttr.needsUpdate = true
      // 法线不每帧重算（O(N) 重操作），水面是平面 (Y=WATER_SURFACE_Y)，法线恒为 (0,1,0)
    },

    _pruneHighTerrainWetness () {
      const geo = this._sandSculptGeo
      if (!geo) return false
      const pos = geo.attributes.position
      const wet = geo.attributes.wetness
      if (!pos || !wet) return false
      const arr = pos.array
      const warr = wet.array
      let changed = false
      for (let i = 0; i < warr.length; i++) {
        if (warr[i] > 0 && arr[i * 3 + 1] > WATER_MAX_TERRAIN_Y) {
          warr[i] = 0
          changed = true
        }
      }
      if (changed) wet.needsUpdate = true
      return changed
    },

    _notifyTerrainVisualChange () {
      this._pruneHighTerrainWetness()
      this._syncWaterSurfaceMesh()
      this._rebuildSandSculptSideMesh()
    },

    /**
     * 仅重建笔刷影响范围内的法线：算法复用 _buildWaterSurfaceMesh 的思路，
     * 找出改动的顶点周围的三角形，重算其 normal。
     * 为了简单可靠，直接对整个网格 computeVertexNormals（已是 Three.js 优化版本）。
     * 调用方应避免在每帧调用。
     */
    _markSculptNormalsDirty () {
      // 标记延迟，_flushNormals 每帧只重算一次
      this._normalsDirty = true
    },
    _flushNormals () {
      if (!this._normalsDirty || !this._sandSculptGeo) return
      this._sandSculptGeo.computeVertexNormals()
      this._normalsDirty = false
    },

    /**
     * 侧面 mesh 重建请求：节流到每帧最多一次
     * _rebuildSandSculptSideMesh 是 O(N²) 量级（每帧重建一次即可）
     */
    _requestSideRebuild () {
      // 节流：每帧只在 tick 末尾 _flushSideRebuild 一次重建
      this._sideRebuildPending = true
    },
    _flushSideRebuild () {
      if (!this._sideRebuildPending || !this._sandSculptSideMesh) {
        this._sideRebuildPending = false
        return
      }
      // 进一步节流：连续笔刷时每 N 帧才重建一次（其余帧依赖旧几何体的轻量更新）
      this._sideRebuildCooldown = (this._sideRebuildCooldown || 0) + 1
      if (this._sideRebuildCooldown < 2) {
        // 跳过本次，保留待重建标记
        return
      }
      this._rebuildSandSculptSideMesh()
      this._sideRebuildPending = false
      this._sideRebuildCooldown = 0
    },

    _sampleWetnessLocal (x, z) {
      const geo = this._sandSculptGeo
      if (!geo) return 0
      const wet = geo.attributes.wetness
      const pos = geo.attributes.position
      if (!wet || !pos) return 0
      const warr = wet.array
      const arr = pos.array
      let bestD2 = Infinity
      let bestW = 0
      const n = pos.count
      for (let i = 0; i < n; i++) {
        const i3 = i * 3
        const dx = arr[i3] - x
        const dz = arr[i3 + 2] - z
        const d2 = dx * dx + dz * dz
        if (d2 < bestD2) {
          bestD2 = d2
          bestW = warr[i]
        }
      }
      return bestW
    },

    /**
     * 堆沙笔刷：在沙面细分网格上沿 Y 缓慢抬高，形成小山丘（按住不动也会由 tick 持续叠加）
     * @param {THREE.Vector3} worldVec
     * @param {number} strengthMul 相对 SCULPT_BASE_LIFT 的倍率
     */
    _trySculptAtWorld (worldVec, strengthMul = 1) {
      if (this.terrainTool !== 'sculpt' || !this._sandSculptMesh || !this._sandSculptGeo || !worldVec) return
      const geo = this._sandSculptGeo
      const posAttr = geo.attributes.position
      if (!posAttr) return
      const wetAttr = geo.attributes.wetness
      const warr = wetAttr ? wetAttr.array : null
      const arr = posAttr.array
      this.terrainGroup.worldToLocal(this._sculptCenterLocal.copy(worldVec))
      const bx = this._sculptCenterLocal.x
      const bz = this._sculptCenterLocal.z
      const R = SCULPT_BRUSH_R
      const R2 = R * R
      const base = SCULPT_BASE_LIFT * strengthMul
      const maxY = SCULPT_MAX_Y
      const invR = 1 / R
      let changed = false
      let wetChanged = false

      // 用顶点空间桶：只遍历笔刷附近的顶点
      const grid = this._vertGrid
      const candidates = []
      if (grid) {
        const minX = bx - R
        const maxX = bx + R
        const minZ = bz - R
        const maxZ = bz + R
        const c0 = Math.max(0, Math.floor((minX + grid.halfX + grid.cell) / grid.cell))
        const c1 = Math.min(grid.cols - 1, Math.floor((maxX + grid.halfX + grid.cell) / grid.cell))
        const r0 = Math.max(0, Math.floor((minZ + grid.halfZ + grid.cell) / grid.cell))
        const r1 = Math.min(grid.rows - 1, Math.floor((maxZ + grid.halfZ + grid.cell) / grid.cell))
        for (let r = r0; r <= r1; r++) {
          for (let c = c0; c <= c1; c++) {
            const list = grid.buckets[r * grid.cols + c]
            if (list) for (let k = 0; k < list.length; k++) candidates.push(list[k])
          }
        }
      }
      const nVerts = candidates.length || posAttr.count
      let peakY = 0
      for (let idx = 0; idx < nVerts; idx++) {
        const i = candidates.length ? candidates[idx] : idx
        const i3 = i * 3
        const vx = arr[i3]
        const vy = arr[i3 + 1]
        const vz = arr[i3 + 2]
        const dx = vx - bx
        const dz = vz - bz
        const d2 = dx * dx + dz * dz
        if (d2 > R2) continue
        const d = Math.sqrt(d2)
        const u = 1 - d * invR
        if (u <= 0) continue
        const smooth = u * u * (3 - 2 * u)
        const bell = Math.exp(-(d * invR) * (d * invR) * 2.0)
        const falloff = smooth * bell
        const add = base * falloff
        if (add < 1e-9) continue
        const newY = Math.min(maxY, vy + add)
        arr[i3 + 1] = newY
        changed = true
        if (newY > peakY) peakY = newY
        if (warr && newY > WATER_MAX_TERRAIN_Y && warr[i] > 0) {
          warr[i] = 0
          wetChanged = true
        }
      }
      if (changed) {
        posAttr.needsUpdate = true
        this._markSculptNormalsDirty()
      }
      if (wetChanged) wetAttr.needsUpdate = true
      if (changed) this._requestSideRebuild()
      if (wetChanged) this._syncWaterSurfaceMesh()
      if (changed && typeof this._emitSculptHeight === 'function') {
        this._emitSculptHeight(peakY)
      }
    },

    /** 把 sculpt 区域最高顶点 Y 转成米，emit 给父组件 — Cursor Write It */
    _emitSculptHeight (peakY) {
      const meters = (peakY / SCULPT_MAX_Y) * 50
      this.$emit('terrain-sculpt-height', { meters })
    },

    /**
     * 水面笔刷：向下挖坑并标记 wetness，池顶由半透明水面层覆盖
     */
    _tryWaterAtWorld (worldVec, strengthMul = 1) {
      if (this.terrainTool !== 'water' || !this._sandSculptMesh || !this._sandSculptGeo || !worldVec) return
      const geo = this._sandSculptGeo
      const posAttr = geo.attributes.position
      const wetAttr = geo.attributes.wetness
      if (!posAttr || !wetAttr) return
      const arr = posAttr.array
      const warr = wetAttr.array
      this.terrainGroup.worldToLocal(this._sculptCenterLocal.copy(worldVec))
      const bx = this._sculptCenterLocal.x
      const bz = this._sculptCenterLocal.z
      const R = SCULPT_BRUSH_R * WATER_BRUSH_R_MUL
      const sigma = R * WATER_SIGMA_MUL
      const sigma2 = Math.max(1e-6, 2 * sigma * sigma)
      const cut = R * 1.2
      const cut2 = cut * cut
      const baseWet = WATER_WET_STAMP_BASE * strengthMul
      const pullBase = WATER_DIG_PULL * strengthMul
      let posChanged = false
      let wetChanged = false

      // 用顶点空间桶：只遍历笔刷附近的顶点
      const grid = this._vertGrid
      const candidates = []
      if (grid) {
        const minX = bx - cut
        const maxX = bx + cut
        const minZ = bz - cut
        const maxZ = bz + cut
        const c0 = Math.max(0, Math.floor((minX + grid.halfX + grid.cell) / grid.cell))
        const c1 = Math.min(grid.cols - 1, Math.floor((maxX + grid.halfX + grid.cell) / grid.cell))
        const r0 = Math.max(0, Math.floor((minZ + grid.halfZ + grid.cell) / grid.cell))
        const r1 = Math.min(grid.rows - 1, Math.floor((maxZ + grid.halfZ + grid.cell) / grid.cell))
        for (let r = r0; r <= r1; r++) {
          for (let c = c0; c <= c1; c++) {
            const list = grid.buckets[r * grid.cols + c]
            if (list) for (let k = 0; k < list.length; k++) candidates.push(list[k])
          }
        }
      }
      const nVerts = candidates.length || posAttr.count
      for (let idx = 0; idx < nVerts; idx++) {
        const i = candidates.length ? candidates[idx] : idx
        const i3 = i * 3
        const vx = arr[i3]
        const vy = arr[i3 + 1]
        const vz = arr[i3 + 2]
        if (vy > WATER_MAX_TERRAIN_Y) continue
        const dx = vx - bx
        const dz = vz - bz
        const d2 = dx * dx + dz * dz
        if (d2 > cut2) continue
        const falloff = Math.exp(-d2 / sigma2)
        if (falloff < 1e-5) continue
        const stamp = baseWet * falloff * (0.85 + 0.15 * falloff)
        const ow = warr[i]
        const nw = Math.min(1, ow + stamp * (1.0 - ow * 0.5))
        if (nw > ow + 1e-6) {
          warr[i] = nw < 0.06 ? 0 : nw
          wetChanged = true
        }
        if (nw < 0.08 && falloff < 0.08) continue
        const profileY = -WATER_MAX_DEPTH * falloff * Math.max(nw, falloff)
        const desiredY = Math.min(WATER_SURFACE_Y - 0.004, profileY)
        const pull = Math.min(1, pullBase * (0.45 + 0.55 * falloff))
        const newY = vy + (desiredY - vy) * pull
        const clampedY = Math.max(-WATER_MAX_DEPTH, Math.min(WATER_SURFACE_Y - 0.004, newY))
        if (Math.abs(clampedY - vy) > 1e-9) {
          arr[i3 + 1] = clampedY
          posChanged = true
        }
      }
      if (posChanged) {
        posAttr.needsUpdate = true
        this._markSculptNormalsDirty()
      }
      if (wetChanged) wetAttr.needsUpdate = true
      if (posChanged) this._requestSideRebuild()
      if (wetChanged) this._syncWaterSurfaceMesh()
      // 跳过完整 _notifyTerrainVisualChange 中的 _pruneHighTerrainWetness 全量扫描
    },

    _getMaterialTypeIndex () {
      const idx = MATERIAL_TYPE_INDEX[this.terrainMaterial]
      return typeof idx === 'number' ? idx : 0
    },

    /**
     * 材质笔刷：与堆沙同尺寸/柔边，渐进混合 matType
     */
    _tryMaterialAtWorld (worldVec, strengthMul = 1) {
      if (this.terrainTool !== 'material' || !this._sandSculptMesh || !this._sandSculptGeo || !worldVec) {
        return
      }
      const geo = this._sandSculptGeo
      const matAttr = geo.attributes.matType
      const wetAttr = geo.attributes.wetness
      const posAttr = geo.attributes.position
      if (!matAttr || !posAttr) return
      const marr = matAttr.array
      const warr = wetAttr ? wetAttr.array : null
      const parr = posAttr.array
      const target = this._getMaterialTypeIndex()
      this.terrainGroup.worldToLocal(this._sculptCenterLocal.copy(worldVec))
      const bx = this._sculptCenterLocal.x
      const bz = this._sculptCenterLocal.z
      const R = SCULPT_BRUSH_R * MATERIAL_BRUSH_R_MUL
      const sigma = R * MATERIAL_SIGMA_MUL
      const sigma2 = Math.max(1e-6, 2 * sigma * sigma)
      const cut = R * MATERIAL_CUT_MUL
      const cut2 = cut * cut
      const invR = 1 / R
      const blendBase = MATERIAL_BLEND_BASE * strengthMul
      let matChanged = false
      let wetChanged = false
      const grid = this._vertGrid
      // 笔刷涉及的桶：cell = 笔刷直径 * 1.5，所以一个圆最多跨 3x3 = 9 桶
      const candidates = []
      if (grid) {
        const minX = bx - R
        const maxX = bx + R
        const minZ = bz - R
        const maxZ = bz + R
        const c0 = Math.max(0, Math.floor((minX + grid.halfX + grid.cell) / grid.cell))
        const c1 = Math.min(grid.cols - 1, Math.floor((maxX + grid.halfX + grid.cell) / grid.cell))
        const r0 = Math.max(0, Math.floor((minZ + grid.halfZ + grid.cell) / grid.cell))
        const r1 = Math.min(grid.rows - 1, Math.floor((maxZ + grid.halfZ + grid.cell) / grid.cell))
        for (let r = r0; r <= r1; r++) {
          for (let c = c0; c <= c1; c++) {
            const list = grid.buckets[r * grid.cols + c]
            if (list) for (let k = 0; k < list.length; k++) candidates.push(list[k])
          }
        }
      }
      const nVerts = candidates.length || matAttr.count
      for (let idx = 0; idx < nVerts; idx++) {
        const i = candidates.length ? candidates[idx] : idx
        const i3 = i * 3
        const vx = parr[i3]
        const vy = parr[i3 + 1]
        const vz = parr[i3 + 2]
        const wet = warr ? warr[i] : 0
        const isPoolBottom = wet > 0.12 && vy < -0.006
        const isDryLand = wet <= 0.12
        if (!isPoolBottom && !isDryLand) continue
        const dx = vx - bx
        const dz = vz - bz
        const d2 = dx * dx + dz * dz
        if (d2 > cut2) continue
        const d = Math.sqrt(d2)
        const u = 1 - d * invR
        const smooth = u > 0 ? u * u * (3 - 2 * u) : 0
        const bell = Math.exp(-(d * invR) * (d * invR) * 2.0)
        const gauss = Math.exp(-d2 / sigma2)
        const falloff = Math.max(smooth * bell, gauss * 0.9)
        const blend = Math.min(1, blendBase * falloff)
        if (blend < 1e-5) continue
        const cur = marr[i]
        if (cur !== target) {
          marr[i] = target
          matChanged = true
        }
        if (warr && isDryLand && warr[i] > 0) {
          warr[i] = Math.max(0, warr[i] * (1 - blend * 0.85))
          if (warr[i] < 0.06) warr[i] = 0
          wetChanged = true
        }
      }
      // 合并多次背包 needsUpdate：每帧第一次才 push
      if (matChanged) {
        matAttr.needsUpdate = true
        this._matAttrDirtyFrames = (this._matAttrDirtyFrames || 0) + 1
      }
      if (wetChanged && wetAttr) {
        wetAttr.needsUpdate = true
        this._wetAttrDirtyFrames = (this._wetAttrDirtyFrames || 0) + 1
        // wet 改变才需要刷新水面 mesh，材质笔刷只跑 water sync 不重建 side
        this._syncWaterSurfaceMesh()
      }
      // material 笔刷不改形状，跳过 _rebuildSandSculptSideMesh（每帧重建代价巨大）
    },

    /**
     * 平整笔刷：仅抹平堆沙高度与水面，保留地表材质
     */
    _tryFlattenAtWorld (worldVec, strengthMul = 1) {
      if (this.terrainTool !== 'flatten' || !this._sandSculptMesh || !this._sandSculptGeo || !worldVec) return
      const geo = this._sandSculptGeo
      const posAttr = geo.attributes.position
      const wetAttr = geo.attributes.wetness
      const colorAttr = geo.attributes.color
      if (!posAttr || !wetAttr) return
      const arr = posAttr.array
      const warr = wetAttr.array
      const carr = colorAttr ? colorAttr.array : null
      this.terrainGroup.worldToLocal(this._sculptCenterLocal.copy(worldVec))
      const bx = this._sculptCenterLocal.x
      const bz = this._sculptCenterLocal.z
      const R = SCULPT_BRUSH_R * FLATTEN_BRUSH_R_MUL
      const sigma = R * FLATTEN_SIGMA_MUL
      const sigma2 = Math.max(1e-6, 2 * sigma * sigma)
      const cut = R * 1.2
      const cut2 = cut * cut
      const hRel = FLATTEN_HEIGHT_RELAX * strengthMul
      const wRel = FLATTEN_WET_RELAX * strengthMul
      const cRel = FLATTEN_COLOR_RELAX * strengthMul
      let posChanged = false
      let wetChanged = false
      let colorChanged = false

      // 用顶点空间桶：只遍历笔刷附近的顶点
      const grid = this._vertGrid
      const candidates = []
      if (grid) {
        const minX = bx - cut
        const maxX = bx + cut
        const minZ = bz - cut
        const maxZ = bz + cut
        const c0 = Math.max(0, Math.floor((minX + grid.halfX + grid.cell) / grid.cell))
        const c1 = Math.min(grid.cols - 1, Math.floor((maxX + grid.halfX + grid.cell) / grid.cell))
        const r0 = Math.max(0, Math.floor((minZ + grid.halfZ + grid.cell) / grid.cell))
        const r1 = Math.min(grid.rows - 1, Math.floor((maxZ + grid.halfZ + grid.cell) / grid.cell))
        for (let r = r0; r <= r1; r++) {
          for (let c = c0; c <= c1; c++) {
            const list = grid.buckets[r * grid.cols + c]
            if (list) for (let k = 0; k < list.length; k++) candidates.push(list[k])
          }
        }
      }
      const nVerts = candidates.length || posAttr.count
      for (let idx = 0; idx < nVerts; idx++) {
        const i = candidates.length ? candidates[idx] : idx
        const i3 = i * 3
        const vx = arr[i3]
        const vy = arr[i3 + 1]
        const vz = arr[i3 + 2]
        const dx = vx - bx
        const dz = vz - bz
        const d2 = dx * dx + dz * dz
        if (d2 > cut2) continue
        const falloff = Math.exp(-d2 / sigma2)
        if (falloff < 1e-5) continue
        const core = falloff > 0.28

        const hk = Math.min(core ? 0.55 : 0.32, hRel * falloff)
        if (hk > 1e-8 && Math.abs(vy) > 1e-7) {
          arr[i3 + 1] = vy * (1 - hk)
          if (Math.abs(arr[i3 + 1]) < FLATTEN_HEIGHT_ZERO) arr[i3 + 1] = 0
          posChanged = true
        }

        if (warr[i] > 1e-6) {
          const dw = Math.min(core ? 0.72 : 0.45, wRel * falloff * (0.8 + 0.2 * warr[i]))
          warr[i] = Math.max(0, warr[i] - dw)
          if (warr[i] < FLATTEN_WET_ZERO) warr[i] = 0
          wetChanged = true
        }

        if (carr && cRel > 1e-8) {
          const cm = Math.min(0.22, cRel * falloff)
          for (let j = 0; j < 3; j++) {
            const ci = i3 + j
            const c = carr[ci]
            if (c < 0.999) {
              carr[ci] += (1 - c) * cm
              colorChanged = true
            }
          }
        }
      }
      if (posChanged) {
        posAttr.needsUpdate = true
        this._markSculptNormalsDirty()
      }
      if (wetChanged) wetAttr.needsUpdate = true
      if (colorChanged && colorAttr) colorAttr.needsUpdate = true
      if (posChanged) this._requestSideRebuild()
      if (wetChanged) this._syncWaterSurfaceMesh()
    },

    _isTerrainEditActive () {
      return !!(this.terrainVisible && this.terrainTool && this.sandboxRoot && this.renderer)
    },

    _isSandPropPlaceActive () {
      return !!(this.sandPlaceItem && this.sandboxRoot && this.renderer)
    },

    _isFinePointerDevice () {
      if (typeof window === 'undefined') return true
      return window.matchMedia('(pointer: fine)').matches
    },

    _applyCanvasCursor (el, cursor = 'default') {
      if (!el) return
      if (!this._isFinePointerDevice()) {
        el.style.cursor = ''
        return
      }
      if (this.isTourModeActive() && this._isTourPointerLocked()) {
        el.style.cursor = 'none'
        return
      }
      el.style.cursor = cursor
    },

    _syncTerrainCursor () {
      if (!this.renderer || !this.renderer.domElement) return
      const el = this.renderer.domElement
      if (this._pointer && this._pointer.mode) return
      if (!this._isFinePointerDevice()) {
        el.style.cursor = ''
        return
      }
      if (this.isTourModeActive()) {
        el.style.cursor = this._isTourPointerLocked() ? 'none' : 'default'
        return
      }
      el.style.cursor = 'default'
    },

    clearPointerMode () {
      if (!this._pointer) return
      this._pointer.mode = null
      this._pointer.terrainLastOk = false
      this._syncTerrainCursor()
    },

    /**
     * 射线与沙盘表面求交，世界坐标写入 writeVec。
     * 优先打真实堆沙网格（山点山、平地点平地）；未命中时再退回水平基准面。
     */
    _raycastSandboardSurface (clientX, clientY, writeVec) {
      if (!this.sandboxRoot || !this.renderer || !this.camera) return false
      const hx = this._islandHalfX
      const hz = this._islandHalfZ
      if (typeof hx !== 'number' || typeof hz !== 'number') return false
      const canvas = this.renderer.domElement
      const rect = canvas.getBoundingClientRect()
      this._ndc.x = ((clientX - rect.left) / rect.width) * 2 - 1
      this._ndc.y = -((clientY - rect.top) / rect.height) * 2 + 1
      this._raycaster.setFromCamera(this._ndc, this.camera)

      // 直接对堆沙网格求交：避免「先打水平面再补高度」导致点击山体时 XZ 偏到山后平地
      if (this._sandSculptMesh) {
        const mesh = this._sandSculptMesh
        const prevVisible = mesh.visible
        mesh.visible = true
        const hits = this._raycaster.intersectObject(mesh, false)
        mesh.visible = prevVisible
        if (hits.length > 0) {
          this._terrainHit.copy(hits[0].point)
          this.sandboxRoot.worldToLocal(this._terrainHit)
          this._terrainHit.x = Math.max(-hx, Math.min(hx, this._terrainHit.x))
          this._terrainHit.z = Math.max(-hz, Math.min(hz, this._terrainHit.z))
          const sculptH = this._sampleTerrainFieldBilinear(
            this._terrainHit.x,
            this._terrainHit.z,
            'height'
          )
          // terrainGroup.position.y = _terrainLocalY，与 _getPropGroundLocalY 保持一致
          this._terrainHit.y = (this._terrainLocalY || this._sandCenterLocalY || 0) + sculptH
          this.sandboxRoot.localToWorld(this._terrainHit)
          writeVec.copy(this._terrainHit)
          return true
        }
      }

      const r = this._raycaster.ray
      const dy = r.direction.y
      if (Math.abs(dy) < 1e-8) return false
      const ySurf = this._getSandSurfaceWorldY()
      const t = (ySurf - r.origin.y) / dy
      if (t < 0) return false
      writeVec.copy(r.origin).addScaledVector(r.direction, t)
      this._terrainHit.copy(writeVec)
      this.sandboxRoot.worldToLocal(this._terrainHit)
      this._terrainHit.x = Math.max(-hx, Math.min(hx, this._terrainHit.x))
      this._terrainHit.z = Math.max(-hz, Math.min(hz, this._terrainHit.z))
      const sculptH = this._sampleTerrainFieldBilinear(
        this._terrainHit.x,
        this._terrainHit.z,
        'height'
      )
      // terrainGroup.position.y = _terrainLocalY，与 _getPropGroundLocalY 保持一致
      this._terrainHit.y = (this._terrainLocalY || this._sandCenterLocalY || 0) + sculptH
      this.sandboxRoot.localToWorld(this._terrainHit)
      writeVec.copy(this._terrainHit)
      return true
    },

    _sampleSculptHeightLocal (x, z) {
      if (!this._sandSculptGeo) return 0
      const pos = this._sandSculptGeo.attributes.position
      if (!pos) return 0
      const arr = pos.array
      let bestD2 = Infinity
      let bestY = 0
      const n = pos.count
      for (let i = 0; i < n; i++) {
        const i3 = i * 3
        const dx = arr[i3] - x
        const dz = arr[i3 + 2] - z
        const d2 = dx * dx + dz * dz
        if (d2 < bestD2) {
          bestD2 = d2
          bestY = arr[i3 + 1]
        }
      }
      return bestY
    },

    _getPropGroundLocalY (localX, localZ, heightOffset = 0) {
      const sculptH = this._sampleTerrainFieldBilinear(localX, localZ, 'height')
      // terrainGroup.position.y = _terrainLocalY，与 _raycastSandboardSurface 保持一致
      const baseY = typeof this._terrainLocalY === 'number'
        ? this._terrainLocalY
        : (typeof this._sandCenterLocalY === 'number' ? this._sandCenterLocalY : 0)
      return baseY + sculptH + heightOffset
    },

    /** 按实际顶点底面（含骨骼姿态）贴齐沙盘表面；须在 prop 已挂到 sandboxRoot 子树后调用 */
    _snapPropBottomToGround (prop, groundY) {
      if (!prop || !this.sandboxRoot) return
      prop.updateMatrixWorld(true)
      this.sandboxRoot.updateMatrixWorld(true)
      const bottomY = getSandPropBottomLocalY(prop, this.sandboxRoot)
      const adjustment = groundY + 0.004 - bottomY
      prop.position.y += adjustment
    },

    _reapplyAllPropTransforms () {
      if (!this._sandPropsGroup) return
      this._sandPropsGroup.children.forEach((prop) => {
        this._applyPropTransform(prop, { resetPose: true })
      })
    },

    _resolvePropSandboxPosition (worldHit, target) {
      target.copy(worldHit)
      this.sandboxRoot.worldToLocal(target)
      target.y = this._getPropGroundLocalY(target.x, target.z)
      return target
    },

    _initSandPropUserData (prop, item) {
      _sandPropIdSeq += 1
      this._sandPropPlaceOrder = (this._sandPropPlaceOrder || 0) + 1
      prop.userData.sandProp = {
        id: _sandPropIdSeq,
        label: item.label,
        emoji: item.emoji,
        modelKey: item.modelKey,
        modelUrl: item.modelUrl,
        previewUrl: item.previewUrl,
        category: item.category,
        key: item.key,
        placedAt: Math.floor(Date.now() / 1000),
        putDownOrder: this._sandPropPlaceOrder
      }
      const modelRef = item.modelKey || item.modelUrl
      const spec = modelRef ? getSandModelSpec(modelRef) : null
      const pitchMax = resolvePropPitchMax(spec)
      const defaultPitch =
        spec && typeof spec.defaultPropPitch === 'number'
          ? spec.defaultPropPitch
          : 0
      const defaultRotation =
        spec && typeof spec.defaultPropYaw === 'number'
          ? spec.defaultPropYaw
          : 0
      const defaultHeightOffset =
        spec && spec.groundLift != null ? spec.groundLift : 0
      prop.userData.editState = {
        scale: 1,
        rotation: defaultRotation,
        heightOffset: defaultHeightOffset,
        pitch: THREE.MathUtils.clamp(defaultPitch, -pitchMax, pitchMax)
      }
      prop.userData.sandHumanCharacter = !!(spec && spec.humanCharacter)
      const inner = prop.userData.animModel
      if (prop.userData.sandHumanCharacter && inner) {
        const baked =
          typeof inner.rotation.y === 'number' ? inner.rotation.y : 0
        if (Math.abs(baked) > 0.001) {
          prop.userData.editState.rotation = baked
          inner.rotation.y = 0
          inner.updateMatrixWorld(true)
        }
      }
    },

    /** 编辑人物沙具时，转到正面 45° 俯视；orbit 枢轴保持沙盘中心，避免点击/拖动时视角漂移 */
    _orbitCameraToPropFront (prop) {
      if (!prop || !this.camera || !this.sandboxRoot || !this._orbit) return
      const preservedRadius = this.getCameraOrbitRadius()
      this.getGroundCenter(this._orbit.target)
      const pitchDeg = THREE.MathUtils.radToDeg(CAMERA_DEFAULT_ELEVATION)
      this._applyOrbitFromAngles(0, pitchDeg, preservedRadius)
    },

    _applyPropTransform (prop, options = {}) {
      if (!prop || !prop.userData.editState) return
      const inner = prop.userData.animModel
      const spec =
        prop.userData.sandModelSpec ||
        getSandModelSpec(
          (prop.userData.sandProp && (prop.userData.sandProp.modelKey || prop.userData.sandProp.modelUrl)) || ''
        )
      if (inner && spec) fixVolumetricFishFacing(inner, spec)
      if (inner && spec) ensureLayFlatCritterPose(inner, spec)
      const st = prop.userData.editState
      const s = PROP_BASE_SCALE * st.scale
      const pitchMax = resolvePropPitchMax(spec)
      const pitch = THREE.MathUtils.clamp(st.pitch || 0, -pitchMax, pitchMax)
      const useModelRotation =
        prop.userData.sandPropKind === 'model' ||
        prop.userData.sandPropKind === 'preview-plane'
      if (useModelRotation) {
        prop.scale.set(s, s, s)
        prop.rotation.order = 'YXZ'
        prop.rotation.y = st.rotation
        prop.rotation.x = pitch
        prop.rotation.z = 0
      } else {
        prop.scale.set(s, s, 1)
        prop.rotation.x = pitch
        prop.rotation.z = st.rotation
      }
      if (options.resetPose) resetSandPropPose(prop)
      // heightOffset 是单独的视觉偏移（沉入地面），不混入 groundY
      const baseGroundY = this._getPropGroundLocalY(
        prop.position.x,
        prop.position.z,
        0
      )
      prop.position.y = baseGroundY
      this._snapPropBottomToGround(prop, baseGroundY)
      prop.position.y += st.heightOffset
      prop.updateMatrixWorld(true)
      prop.renderOrder = 30
      // 入地时（heightOffset < 0）加软蓝色轮廓光，浮出地面后自动消除
      const isBuried = (st.heightOffset || 0) < -0.001
      this._setBuriedOutline(prop, isBuried)
    },

    /** 给入地模型加软蓝轮廓光（模拟天空透射边缘光），出地面后自动清除 */
    _setBuriedOutline (prop, on) {
      if (!prop) return
      const meshes = []
      prop.traverse(obj => {
        if (obj.isMesh) meshes.push(obj)
      })
      const edgeColor = new THREE.Color(0x90caf9)
      const black = new THREE.Color(0x000000)
      for (const mesh of meshes) {
        const mat = mesh.material
        if (!mat) continue
        const mats = Array.isArray(mat) ? mat : [mat]
        for (const m of mats) {
          if (m.emissive) {
            // 仅在没有编辑高亮（golden）时显示轮廓光
            const isEditing = prop === this._editingProp
            if (!isEditing) {
              m.emissive.copy(on ? edgeColor : black)
              m.emissiveIntensity = on ? 0.22 : 0
            }
          }
        }
      }
    },

    _clampPropSandboxXZ (localVec) {
      const hx = this._islandHalfX
      const hz = this._islandHalfZ
      if (typeof hx === 'number') {
        localVec.x = Math.max(-hx, Math.min(hx, localVec.x))
      }
      if (typeof hz === 'number') {
        localVec.z = Math.max(-hz, Math.min(hz, localVec.z))
      }
      return localVec
    },

    _movePropToWorldHit (sprite, worldHit) {
      if (!sprite || !worldHit) return
      const local = this._resolvePropSandboxPosition(worldHit, this._sculptCenterLocal)
      this._clampPropSandboxXZ(local)
      sprite.position.x = local.x
      sprite.position.z = local.z
      this._applyPropTransform(sprite)
      if (this._editingProp === sprite) {
        this._emitPropEditHud()
      }
    },

_raycastSandProp (clientX, clientY) {
      if (!this._sandPropsGroup || !this._sandPropsGroup.children.length) return null
      if (!this.renderer || !this.camera) return null
      const canvas = this.renderer.domElement
      const rect = canvas.getBoundingClientRect()
      this._ndc.x = ((clientX - rect.left) / rect.width) * 2 - 1
      this._ndc.y = -((clientY - rect.top) / rect.height) * 2 + 1
      this._raycaster.setFromCamera(this._ndc, this.camera)
      // 先射线检测 prop 本体；如果命中失败但点中了圆环指示器，也返回对应 sprite
      const hits = this._raycaster.intersectObjects(this._sandPropsGroup.children, true)
      for (let i = 0; i < hits.length; i++) {
        let obj = hits[i].object
        while (obj) {
          if (obj.userData && obj.userData.sandProp) return obj
          if (obj === this._sandPropsGroup) break
          obj = obj.parent
        }
      }
      // 点击落到了圆环 (sandPropIndicator)：任一圆环命中即返回其绑定的 sprite
      if (this._propIndicatorGroups && this._propIndicatorGroups.size) {
        const groups = []
        this._propIndicatorGroups.forEach((g) => groups.push(g))
        const ringHits = this._raycaster.intersectObjects(groups, true)
        if (ringHits.length > 0) {
          // 从命中对象向上找到绑定的 sprite
          let obj = ringHits[0].object
          while (obj) {
            if (obj.userData && obj.userData.targetSprite) return obj.userData.targetSprite
            obj = obj.parent
          }
        }
      }
      return null
    },

    _getPropHudAnchorWorld (sprite, writeVec) {
      if (!sprite || !writeVec || !this.sandboxRoot) {
        return writeVec ? writeVec.set(0, 0, 0) : null
      }
      writeVec.set(
        sprite.position.x,
        this._getPropGroundLocalY(sprite.position.x, sprite.position.z, 0),
        sprite.position.z
      )
      return this.sandboxRoot.localToWorld(writeVec)
    },

    _projectPropToScreen (sprite) {
      if (!sprite || !this.renderer || !this.camera) {
        return { screenX: 0, screenY: 0 }
      }
      this._getPropHudAnchorWorld(sprite, this._propWorldScratch)
      this._propWorldScratch.project(this.camera)
      const canvas = this.renderer.domElement
      const rect = canvas.getBoundingClientRect()
      return {
        screenX: rect.left + (this._propWorldScratch.x * 0.5 + 0.5) * rect.width,
        screenY: rect.top + (-this._propWorldScratch.y * 0.5 + 0.5) * rect.height
      }
    },

    /** HUD 锚点：贴地位置，高度滑条时控件不上下漂移 — Cursor Write It */
    _projectPropHudAnchorToScreen (sprite) {
      if (!sprite || !this.renderer || !this.camera) {
        return { screenX: 0, screenY: 0 }
      }
      this._getPropHudAnchorWorld(sprite, this._propWorldScratch)
      this._propWorldScratch.project(this.camera)
      const canvas = this.renderer.domElement
      const rect = canvas.getBoundingClientRect()
      return {
        screenX: rect.left + (this._propWorldScratch.x * 0.5 + 0.5) * rect.width,
        screenY: rect.top + (-this._propWorldScratch.y * 0.5 + 0.5) * rect.height
      }
    },

    _emitPropEditHud (followScreen = true) {
      if (!this._editingProp) {
        this.$emit('sand-prop-edit', { active: false })
        return
      }
      if (!followScreen) return
      const screen = this._projectPropHudAnchorToScreen(this._editingProp)
      const meta = this._editingProp.userData.sandProp || {}
      this.$emit('sand-prop-edit', {
        active: true,
        id: meta.id,
        label: meta.label,
        screenX: screen.screenX,
        screenY: screen.screenY
      })
    },

    /** 读取当前编辑中沙具的姿态 — Cursor Write It */
    getPropEditState () {
      if (!this._editingProp || !this._editingProp.userData.editState) return null
      const st = this._editingProp.userData.editState
      return {
        rotation: st.rotation,
        pitch: st.pitch || 0,
        scale: st.scale,
        heightOffset: st.heightOffset
      }
    },

    /** 设置离地高度偏移 — Cursor Write It */
    setPropEditHeight (heightOffset) {
      const sprite = this._editingProp
      if (!sprite || !sprite.userData.editState) return
      sprite.userData.editState.heightOffset = THREE.MathUtils.clamp(
        heightOffset,
        PROP_HEIGHT_MIN,
        PROP_HEIGHT_MAX
      )
      this._clampPropSandboxXZ(sprite.position)
      this._applyPropTransform(sprite)
    },

    getPropEditPitchMax () {
      return resolvePropPitchMax(getEditingPropSpec(this._editingProp))
    },

    /** 设置俯仰角：正=后仰躺平，负=前倾趴地 — Cursor Write It */
    setPropEditPitch (pitchRad) {
      const sprite = this._editingProp
      if (!sprite || !sprite.userData.editState) return
      const pitchMax = resolvePropPitchMax(getEditingPropSpec(sprite))
      sprite.userData.editState.pitch = THREE.MathUtils.clamp(
        pitchRad,
        -pitchMax,
        pitchMax
      )
      this._clampPropSandboxXZ(sprite.position)
      this._applyPropTransform(sprite)
    },

    /** 左右旋转（弧度增量）— Cursor Write It */
    adjustPropEditRotation (deltaRad) {
      const sprite = this._editingProp
      if (!sprite || !sprite.userData.editState || !deltaRad) return
      sprite.userData.editState.rotation += deltaRad
      this._clampPropSandboxXZ(sprite.position)
      this._applyPropTransform(sprite)
    },

    /** 设置水平旋转角（弧度，0–2π）— Cursor Write It */
    setPropEditRotation (rotationRad) {
      const sprite = this._editingProp
      if (!sprite || !sprite.userData.editState) return
      const twoPi = Math.PI * 2
      sprite.userData.editState.rotation =
        ((rotationRad % twoPi) + twoPi) % twoPi
      this._clampPropSandboxXZ(sprite.position)
      this._applyPropTransform(sprite)
    },

    /** 设置缩放倍率 — Cursor Write It */
    setPropEditScale (scale) {
      const sprite = this._editingProp
      if (!sprite || !sprite.userData.editState) return
      sprite.userData.editState.scale = THREE.MathUtils.clamp(
        scale,
        PROP_SCALE_MIN,
        PROP_SCALE_MAX
      )
      this._clampPropSandboxXZ(sprite.position)
      this._applyPropTransform(sprite)
    },

    /** 给沙具加金色高亮（emissive glow），用于区分编辑中的沙具 */
    _highlightProp (sprite, on) {
      if (!sprite) return
      const meshes = []
      sprite.traverse(obj => {
        if (obj.isMesh) meshes.push(obj)
      })
      if (meshes.length === 0) return
      const golden = new THREE.Color(0xffd54f)
      const black = new THREE.Color(0x000000)
      for (const mesh of meshes) {
        const mat = mesh.material
        if (!mat) continue
        if (Array.isArray(mat)) {
          for (const m of mat) {
            if (m.emissive) {
              m.emissive.copy(on ? golden : black)
              m.emissiveIntensity = on ? 0.45 : 0
            }
          }
        } else if (mat.emissive) {
          mat.emissive.copy(on ? golden : black)
          mat.emissiveIntensity = on ? 0.45 : 0
        }
      }
    },

    /** 显示选中指示器（脚底金色圆环） */
    _showPropIndicator (sprite) {
      // 已存在的同 sprite 圆环不重建
      if (this._propIndicatorGroups && this._propIndicatorGroups.has(sprite)) return
      this._removePropIndicatorFor(sprite)
      const group = new THREE.Group()

      // 根据模型脚底 footprint 自动适配圆环大小
      const footprintRadius = getSandPropFootprintRadius(sprite, this.sandboxRoot)
      const ringRadius = THREE.MathUtils.clamp(footprintRadius * 1.1, 0.12, 0.45)

      // 外圈细环（更细的描边）
      const ringGeo = new THREE.RingGeometry(ringRadius * 0.92, ringRadius, 32)
      const ringMat = new THREE.MeshBasicMaterial({
        color: 0xff8a00,
        side: THREE.DoubleSide,
        depthTest: false,
        depthWrite: false
      })
      const ring = new THREE.Mesh(ringGeo, ringMat)
      ring.rotation.x = -Math.PI / 2
      ring.renderOrder = 998
      group.add(ring)

      // 不可见的大圆盘用作点击命中区域（圆环本身只有一圈线很难点中）
      // 渲染时通过 tick() 把 visible 设为 false（不绘制）；点击时 raycast 用
      // hitArea.layers=2 + raycaster.layers=2 配合，确保只 raycast 命中 hitArea
      const hitRadius = THREE.MathUtils.clamp(footprintRadius * 1.6, 0.18, 0.7)
      const hitGeo = new THREE.CircleGeometry(hitRadius, 32)
      const hitMat = new THREE.MeshBasicMaterial({
        color: 0xff8a00,            // 与圆环同色，colorWrite false 不会画
        transparent: true,
        opacity: 0,
        side: THREE.DoubleSide,
        depthWrite: false,
        depthTest: false,
        colorWrite: false          // 关键：不写颜色，完全不渲染，但仍可被 raycast 命中
      })
      const hitArea = new THREE.Mesh(hitGeo, hitMat)
      hitArea.rotation.x = -Math.PI / 2
      hitArea.renderOrder = -1
      hitArea.userData.isPropIndicatorHitArea = true
      group.add(hitArea)

      this.sandboxRoot.add(group)
      // sandboxRoot 和 sprite 同级，位置直接用 sprite 的 local 坐标即可
      const baseGroundY = this._getPropGroundLocalY(sprite.position.x, sprite.position.z, 0)
      group.position.set(
        sprite.position.x,
        baseGroundY + 0.01,
        sprite.position.z
      )
      group.userData.targetSprite = sprite
      if (!this._propIndicatorGroups) this._propIndicatorGroups = new Map()
      this._propIndicatorGroups.set(sprite, group)
    },

    /** 移除单个 sprite 对应的圆环 */
    _removePropIndicatorFor (sprite) {
      if (!this._propIndicatorGroups || !sprite) return
      const group = this._propIndicatorGroups.get(sprite)
      if (!group) return
      this.sandboxRoot.remove(group)
      group.traverse(obj => {
        if (obj.geometry) obj.geometry.dispose()
        if (obj.material) {
          if (Array.isArray(obj.material)) obj.material.forEach(m => m.dispose())
          else obj.material.dispose()
        }
      })
      this._propIndicatorGroups.delete(sprite)
    },

    /** 更新选中指示器位置（跟随sprite移动） */
    _updatePropIndicatorPosition () {
      if (!this._propIndicatorGroups || !this._editingProp) return
      const sprite = this._editingProp
      const group = this._propIndicatorGroups.get(sprite)
      if (!group) return
      const baseGroundY = this._getPropGroundLocalY(sprite.position.x, sprite.position.z, 0)
      group.position.set(
        sprite.position.x,
        baseGroundY + 0.01,
        sprite.position.z
      )
    },

    /** 隐藏全部选中指示器 */
    _hidePropIndicator () {
      if (!this._propIndicatorGroups) return
      const sprites = []
      this._propIndicatorGroups.forEach((_, sprite) => sprites.push(sprite))
      sprites.forEach((s) => this._removePropIndicatorFor(s))
      this._propIndicatorGroups.clear()
    },

    startPropEdit (sprite) {
      if (!sprite) return
      this._editingProp = sprite
      this._highlightProp(sprite, true)
      // 显示选中指示箭头
      this._showPropIndicator(sprite)
      if (sprite.userData.sandHumanCharacter) {
        this._orbitCameraToPropFront(sprite)
      }
      const p = sprite.position
      const st = sprite.userData.editState
      this._editSnapshot = {
        x: p.x,
        y: p.y,
        z: p.z,
        editState: {
          scale: st.scale,
          rotation: st.rotation,
          heightOffset: st.heightOffset,
          pitch: st.pitch
        }
      }
      this._emitPropEditHud()
      this._syncTerrainCursor()
      this._notifyCameraOrbitChange()
      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(() => {
          if (this.disposed || this._editingProp !== sprite) return
          this._emitPropEditHud()
          this._notifyCameraOrbitChange()
        })
      }
    },

    endPropEdit (commit) {
      if (!this._editingProp) return
      if (!commit && this._editSnapshot) {
        const p = this._editingProp.position
        const snap = this._editSnapshot
        p.set(snap.x, snap.y, snap.z)
        Object.assign(this._editingProp.userData.editState, snap.editState)
        this._applyPropTransform(this._editingProp)
      }
      const prop = this._editingProp
      this._highlightProp(prop, false)
      // 编辑结束后：根据入地状态重新显示/隐藏轮廓光
      const isBuried = prop && prop.userData.editState
        ? (prop.userData.editState.heightOffset || 0) < -0.001
        : false
      this._setBuriedOutline(prop, isBuried)
      // 取消时隐藏圆环，确认后保持圆环（用户刚操作完的模型需要继续看到选中态）
      if (!commit) this._hidePropIndicator()
      this._editingProp = null
      this._editSnapshot = null
      this.$emit('sand-prop-edit', { active: false })
      this._syncTerrainCursor()
      this._notifyCameraOrbitChange()
    },

    applyPropEditAction (action) {
      const sprite = this._editingProp
      if (!sprite) return
      const st = sprite.userData.editState
      if (!st) return

      switch (action) {
        case 'scaleUp':
          st.scale = Math.min(3.2, st.scale * PROP_SCALE_STEP)
          break
        case 'scaleDown':
          st.scale = Math.max(0.35, st.scale / PROP_SCALE_STEP)
          break
        case 'rotate':
          st.rotation += PROP_ROTATE_STEP
          break
        case 'heightUp':
          st.heightOffset = Math.min(PROP_HEIGHT_MAX, st.heightOffset + PROP_HEIGHT_STEP)
          break
        case 'heightDown':
          st.heightOffset = Math.max(PROP_HEIGHT_MIN, st.heightOffset - PROP_HEIGHT_STEP)
          break
        case 'delete':
          this._highlightProp(sprite, false)
          this._removePropIndicatorFor(sprite)
          disposeSandProp(sprite)
          this._sandPropsGroup.remove(sprite)
          this._editingProp = null
          this._editSnapshot = null
          this.$emit('sand-prop-edit', { active: false })
          this._syncTerrainCursor()
          this._notifyCameraOrbitChange()
          return
        case 'confirm':
          this.endPropEdit(true)
          return
        case 'cancel':
          this.endPropEdit(false)
          return
        default:
          return
      }
      this._clampPropSandboxXZ(sprite.position)
      this._applyPropTransform(sprite)
    },

    async _createSandPropObject (item) {
      const modelRef = item.modelKey || item.modelUrl
      if (modelRef) {
        try {
          const template = await loadSandModel(modelRef)
          const model = cloneSandModel(modelRef, template)
          if (model) return model
          console.warn('[sand prop] clone failed after load', modelRef)
        } catch (err) {
          console.warn('[sand prop] model load failed', modelRef, err)
        }
      }
      const previewUrl = item.previewUrl || item.thumbUrl
      if (previewUrl) {
        const preview = await createPreviewImageSprite(previewUrl)
        if (preview) return preview
      }
      if (modelRef) {
        message.error('3D 模型加载失败，已使用图标代替')
      }
      const sprite = createEmojiSprite(item.emoji || '❓')
      if (sprite) sprite.userData.sandPropKind = 'sprite'
      return sprite
    },

    async _placeSandProp (item, worldHit) {
      if (!item || !this._sandPropsGroup || !this.sandboxRoot) {
        console.log('[PLACE] early return - missing:', { item: !!item, group: !!this._sandPropsGroup, root: !!this.sandboxRoot })
        return null
      }
      const prop = await this._createSandPropObject(item)
      if (!prop) {
        console.log('[PLACE] prop creation failed')
        return null
      }
      const local = this._resolvePropSandboxPosition(worldHit, this._sculptCenterLocal)
      prop.position.copy(local)
      this._initSandPropUserData(prop, item)
      this._sandPropsGroup.add(prop)
      this._applyPropTransform(prop, { resetPose: true })
      return prop
    },

    _getSandSurfaceWorldY () {
      if (!this.sandboxRoot || this._sandCenterLocalY == null) {
        return 0.52
      }
      this._sandSurfWorldScratch.set(0, this._sandCenterLocalY, 0)
      this.sandboxRoot.localToWorld(this._sandSurfWorldScratch)
      return this._sandSurfWorldScratch.y
    },

    /**
     * 射线与草台顶面（沙盘局部 XZ 范围内）求交，结果写入 writeVec（世界坐标）
     */
    _raycastTerrainSurface (clientX, clientY, writeVec) {
      if (!this._isTerrainEditActive()) return false
      return this._raycastSandboardSurface(clientX, clientY, writeVec)
    },

    _emitTerrainInteract (phase, worldVec) {
      const point = worldVec
        ? { x: worldVec.x, y: worldVec.y, z: worldVec.z }
        : null
      this.$emit('terrain-interact', {
        tool: this.terrainTool,
        phase: phase,
        point: point
      })
    },

    onPointerDown (e) {
      if (this.disposed) return
      const btn = e.button
      if (btn !== 0) return

      if (this.isTourTransitioning()) return

      if (this.isTourModeActive()) {
        if (this.isTourInputEnabled() && !this._isTourPointerLocked()) {
          this._requestTourPointerLock()
        }
        return
      }

      if (btn === 0 && this._isSandPropPlaceActive() && !this._isTerrainEditActive()) {
        if (this._raycastSandboardSurface(e.clientX, e.clientY, this._vec)) {
          const hitVec = this._vec.clone()
          const placeItem = { ...this.sandPlaceItem }
          this._placeSandProp(placeItem, hitVec).then((prop) => {
            if (this.disposed) return
            if (prop) this.startPropEdit(prop)
            this.$emit('sand-prop-placed', {
              item: placeItem,
              position: { x: hitVec.x, y: hitVec.y, z: hitVec.z }
            })
            this._notifyCameraOrbitChange()
          })
          return
        }
      }

      if (btn === 0 && !this._isTerrainEditActive() && !this._isSandPropPlaceActive()) {
        const hitProp = this._raycastSandProp(e.clientX, e.clientY)
        if (hitProp) {
          if (this._editingProp !== hitProp) {
            if (this._editingProp) this.endPropEdit(true)
            this.startPropEdit(hitProp)
          }
          this._pointer.mode = 'propDrag'
          this._pointer.lastX = e.clientX
          this._pointer.lastY = e.clientY
          this._pointer.id = e.pointerId
          if (!this.pointerCaptureDisabled) {
            try {
              e.currentTarget.setPointerCapture(e.pointerId)
            } catch (err) {}
          }
          return
        }
        if (this._editingProp) {
          this.endPropEdit(true)
          return
        }
      }

      if (btn === 0 && this._isTerrainEditActive()) {
        if (this._raycastTerrainSurface(e.clientX, e.clientY, this._vec)) {
          this._pointer.mode = 'terrain'
          this._pointer.lastX = e.clientX
          this._pointer.lastY = e.clientY
          this._pointer.id = e.pointerId
          this._pointer.terrainLastOk = true
          this._terrainStroke.copy(this._vec)
          this._terrainStrokePrev.copy(this._vec)
          this._emitTerrainInteract('down', this._vec)
          this._sculptMovedThisFrame = true
          if (this.terrainTool === 'sculpt') {
            this._trySculptAtWorld(this._vec, 1.2)
          } else if (this.terrainTool === 'water') {
            this._tryWaterAtWorld(this._vec, 1.15)
          } else if (this.terrainTool === 'flatten') {
            this._tryFlattenAtWorld(this._vec, 1.1)
          } else if (this.terrainTool === 'material') {
            this._tryMaterialAtWorld(this._vec, 1.2)
          }
          if (!this.pointerCaptureDisabled) {
            try {
              e.currentTarget.setPointerCapture(e.pointerId)
            } catch (err) {}
          }
          return
        }
      }

      if (this._editingProp) return

      this._pointer.mode = 'pan'
      this._pointer.lastX = e.clientX
      this._pointer.lastY = e.clientY
      this._pointer.id = e.pointerId

      if (!this.pointerCaptureDisabled) {
        try {
          e.currentTarget.setPointerCapture(e.pointerId)
        } catch (err) {}
      }
    },

    onPointerMove (e) {
      if (this.disposed || !this._pointer.mode) return
      const prevX = this._pointer.lastX
      const prevY = this._pointer.lastY
      this._pointer.lastX = e.clientX
      this._pointer.lastY = e.clientY

      if (this._pointer.mode === 'propDrag') {
        if (
          this._editingProp &&
          this._raycastSandboardSurface(e.clientX, e.clientY, this._vec)
        ) {
          this._movePropToWorldHit(this._editingProp, this._vec)
          this._emitPropEditHud()
        }
        return
      }

      if (this._pointer.mode === 'terrain') {
        if (this._raycastTerrainSurface(e.clientX, e.clientY, this._vec)) {
          this._pointer.terrainLastOk = true
          this._sculptMovedThisFrame = true
          const d = this._terrainStrokePrev.distanceTo(this._vec)
          if (d < 1e-6) {
            this._terrainStroke.copy(this._vec)
            this._emitTerrainInteract('move', this._vec)
            if (this.terrainTool === 'sculpt') {
              this._trySculptAtWorld(this._vec, 1)
            } else if (this.terrainTool === 'water') {
              this._tryWaterAtWorld(this._vec, 1)
            } else if (this.terrainTool === 'flatten') {
              this._tryFlattenAtWorld(this._vec, 1)
            } else if (this.terrainTool === 'material') {
              this._tryMaterialAtWorld(this._vec, 1)
            }
          } else {
            const n = Math.min(
              TERRAIN_STROKE_MAX_SAMPLES,
              Math.max(1, Math.ceil(d / TERRAIN_STROKE_SAMPLE_STEP))
            )
            const sculptMul = this.terrainTool === 'sculpt' ? 0.78 / Math.pow(n, 0.28) : 0
            const waterMul = this.terrainTool === 'water' ? 0.74 / Math.pow(n, 0.28) : 0
            const flatMul = this.terrainTool === 'flatten' ? 0.72 / Math.pow(n, 0.28) : 0
            for (let i = 1; i <= n; i++) {
              const t = i / n
              this._terrainStrokeLerp.lerpVectors(this._terrainStrokePrev, this._vec, t)
              this._emitTerrainInteract('move', this._terrainStrokeLerp)
              if (this.terrainTool === 'sculpt') {
                this._trySculptAtWorld(this._terrainStrokeLerp, sculptMul)
              } else if (this.terrainTool === 'water') {
                this._tryWaterAtWorld(this._terrainStrokeLerp, waterMul)
              } else if (this.terrainTool === 'flatten') {
                this._tryFlattenAtWorld(this._terrainStrokeLerp, flatMul)
              } else if (this.terrainTool === 'material') {
                this._tryMaterialAtWorld(this._terrainStrokeLerp, 0.85)
              }
            }
            this._terrainStroke.copy(this._vec)
          }
          this._terrainStrokePrev.copy(this._vec)
        }
        return
      }

      if (this._pointer.mode === 'pan') {
        this.panCameraByScreenDelta(prevX, prevY, e.clientX, e.clientY)
      }
    },

    onPointerUp (e) {
      if (!this._pointer) return
      if (this._pointer.mode === 'terrain') {
        const p = this._pointer.terrainLastOk
          ? {
            x: this._terrainStroke.x,
            y: this._terrainStroke.y,
            z: this._terrainStroke.z
          }
          : null
        this.$emit('terrain-interact', {
          tool: this.terrainTool,
          phase: 'up',
          point: p
        })
        this._pointer.terrainLastOk = false
        // 鼠标松开：强制立即重建侧面 + 重算法线 + 同步水面，确保最终状态正确
        this._sideRebuildPending = false
        this._sideRebuildCooldown = 0
        this._flushNormals()
        this._flushWaterSync()
        if (this._sandSculptSideMesh) this._rebuildSandSculptSideMesh()
      }
      this._pointer.mode = null
      this._pointer.id = null
      if (e.currentTarget) {
        this._syncTerrainCursor()
        try {
          if (e.pointerId != null) {
            e.currentTarget.releasePointerCapture(e.pointerId)
          }
        } catch (err) {}
      }
    },

    handleResize () {
      const host = this.$refs.host
      if (!host || !this.renderer || !this.camera) return
      const w = Math.max(1, host.clientWidth)
      const h = Math.max(1, host.clientHeight)
      this.camera.aspect = w / h
      this.camera.updateProjectionMatrix()
      this.renderer.setSize(w, h)
    },

    _makeRainPoints () {
      const count = 96000
      const geo = new THREE.BufferGeometry()
      const positions = new Float32Array(count * 3)
      const speeds = new Float32Array(count)
      const span = 80
      const y1 = 50
      for (let i = 0; i < count; i++) {
        positions[i * 3] = (Math.random() - 0.5) * span
        positions[i * 3 + 1] = 2 + Math.random() * (y1 - 2)
        positions[i * 3 + 2] = (Math.random() - 0.5) * span
        speeds[i] = 10 + Math.random() * 16
      }
      geo.setAttribute('position', new THREE.BufferAttribute(positions, 3))
      const mat = new THREE.PointsMaterial({
        color: 0x9ecbff,
        size: 0.065,
        transparent: true,
        opacity: 0.42,
        depthWrite: false,
        sizeAttenuation: true,
        blending: THREE.AdditiveBlending
      })
      const pts = new THREE.Points(geo, mat)
      pts.userData.speeds = speeds
      pts.userData.yMin = -4
      pts.userData.yMax = y1 + 6
      pts.userData.span = span
      pts.frustumCulled = false
      pts.renderOrder = 50
      return pts
    },

    _makeSnowPoints () {
      const count = 64000
      const geo = new THREE.BufferGeometry()
      const positions = new Float32Array(count * 3)
      const speeds = new Float32Array(count)
      const phases = new Float32Array(count)
      const span = 80
      for (let i = 0; i < count; i++) {
        positions[i * 3] = (Math.random() - 0.5) * span
        positions[i * 3 + 1] = 2 + Math.random() * 48
        positions[i * 3 + 2] = (Math.random() - 0.5) * span
        speeds[i] = 1.2 + Math.random() * 2.4
        phases[i] = Math.random() * Math.PI * 2
      }
      geo.setAttribute('position', new THREE.BufferAttribute(positions, 3))
      const mat = new THREE.PointsMaterial({
        color: 0xffffff,
        size: 0.095,
        transparent: true,
        opacity: 0.72,
        depthWrite: false,
        sizeAttenuation: true
      })
      const pts = new THREE.Points(geo, mat)
      pts.userData.speeds = speeds
      pts.userData.phase = phases
      pts.userData.yMin = -3
      pts.userData.yMax = 56
      pts.userData.span = span
      pts.frustumCulled = false
      pts.renderOrder = 50
      return pts
    },

    _updateRainPoints (dt) {
      const pts = this.rainPoints
      if (!pts || !pts.visible) return
      const pos = pts.geometry.attributes.position
      const arr = pos.array
      const speeds = pts.userData.speeds
      const yMin = pts.userData.yMin
      const yMax = pts.userData.yMax
      const span = pts.userData.span
      const n = speeds.length
      for (let i = 0; i < n; i++) {
        const i3 = i * 3
        arr[i3 + 1] -= speeds[i] * dt
        if (arr[i3 + 1] < yMin) {
          arr[i3 + 1] = yMax + Math.random() * 4
          arr[i3] = (Math.random() - 0.5) * span
          arr[i3 + 2] = (Math.random() - 0.5) * span
        }
      }
      pos.needsUpdate = true
    },

    _updateSnowPoints (dt, worldTime) {
      const pts = this.snowPoints
      if (!pts || !pts.visible) return
      const pos = pts.geometry.attributes.position
      const arr = pos.array
      const speeds = pts.userData.speeds
      const phases = pts.userData.phase
      const yMin = pts.userData.yMin
      const yMax = pts.userData.yMax
      const span = pts.userData.span
      const n = speeds.length
      for (let i = 0; i < n; i++) {
        const i3 = i * 3
        arr[i3 + 1] -= speeds[i] * dt
        const ph = phases[i] + worldTime * 0.85
        arr[i3] += Math.sin(ph + i * 0.1) * 0.35 * dt
        arr[i3 + 2] += Math.cos(ph * 0.8 + i) * 0.28 * dt
        if (arr[i3 + 1] < yMin) {
          arr[i3 + 1] = yMax + Math.random() * 5
          arr[i3] = (Math.random() - 0.5) * span
          arr[i3 + 2] = (Math.random() - 0.5) * span
        }
      }
      pos.needsUpdate = true
    },

    applyWeather (weatherId) {
      if (!this.scene) return
      const preset = WEATHER_PRESETS[weatherId] || WEATHER_PRESETS['day-clear']
      const isNight = weatherId.indexOf('night') === 0

      this.scene.background.setHex(preset.bg)
      if (this.renderer) {
        this.renderer.setClearColor(preset.bg, 1)
      }
      const fg = preset.fog
      if (this.scene.fog) {
        this.scene.fog.color.setHex(fg[0])
        this.scene.fog.near = fg[1]
        this.scene.fog.far = fg[2]
      }

      // 外围海水随天气变暗；沙盘本体仅调光照强度，不改材质基色
      if (this.waterUniforms) {
        const bg = new THREE.Color(preset.bg)
        if (isNight) {
          this.waterUniforms.uDeep.value.copy(bg).multiplyScalar(0.82)
          this.waterUniforms.uColor.value.copy(bg).lerp(new THREE.Color(0x3a5068), 0.28)
          this.waterUniforms.uHighlight.value.copy(bg).lerp(new THREE.Color(0x6a8098), 0.38)
        } else {
          this.waterUniforms.uDeep.value.setHex(0x0288d1)
          this.waterUniforms.uColor.value.setHex(0x29b6f6)
          this.waterUniforms.uHighlight.value.setHex(0xb2ebf2)
        }
      }

      if (this.hemiLight && this.dirLight) {
        const base = LIGHT_DAY_BASE
        const mul = typeof preset.lightMul === 'number' ? preset.lightMul : 1
        this.hemiLight.color.setHex(base.hemiSky)
        this.hemiLight.groundColor.setHex(base.hemiGround)
        this.hemiLight.intensity = base.hemiIntensity * mul
        this.dirLight.color.setHex(base.sunColor)
        this.dirLight.intensity = base.sunIntensity * mul
      }

      const sandboxMul = typeof preset.lightMul === 'number' ? preset.lightMul : 1
      if (this._sandTerrainUniforms) {
        this._sandTerrainUniforms.uLightMul.value = sandboxMul
      }
      if (this._poolWaterUniforms) {
        this._poolWaterUniforms.uLightMul.value = sandboxMul
      }
      if (this._grassSpeckMeshes && this._grassSpeckMeshes.length) {
        const speckOpacity = THREE.MathUtils.lerp(0.52, 1, sandboxMul)
        this._grassSpeckMeshes.forEach((mesh) => {
          if (mesh.material) mesh.material.opacity = speckOpacity
        })
      }
      if (this._grassTopMaterial) {
        this._grassTopMaterial.color.setHex(GRASS_TOP_BASE).multiplyScalar(sandboxMul)
      }
      if (this._sandSculptSideMesh && this._sandSculptSideMesh.material) {
        this._sandSculptSideMesh.material.color
          .setHex(SCULPT_SIDE_COLOR)
          .multiplyScalar(sandboxMul)
      }
      if (this._propFillLight) {
        this._propFillLight.intensity = 0.55 * Math.max(sandboxMul, 0.45)
      }

      const p = preset.precip
      this.precipMode = p
      if (this.rainPoints) {
        this.rainPoints.visible = p === 'rain'
        if (p === 'rain' && this.rainPoints.material) {
          this.rainPoints.material.opacity = isNight ? 0.32 : 0.42
        }
      }
      if (this.snowPoints) {
        this.snowPoints.visible = p === 'snow'
      }
    },

    tick () {
      if (this.disposed || !this.renderer || !this.scene || !this.camera) return
      this.raf = requestAnimationFrame(() => this.tick())
      const nowMs = performance.now()
      const dt = this.lastTickMs ? Math.min(0.08, (nowMs - this.lastTickMs) / 1000) : 0.016
      this.lastTickMs = nowMs
      const t = nowMs * 0.001
      // 选中指示器呼吸动画（所有圆环都参与）
      if (this._propIndicatorGroups && this._propIndicatorGroups.size) {
        const pulse = 0.5 + 0.5 * Math.sin(t * 3)
        const scale = 0.92 + 0.15 * pulse
        this._propIndicatorGroups.forEach((group, sprite) => {
          // 编辑中的跟随移动；其他的不动
          if (sprite === this._editingProp) {
            const baseGroundY = this._getPropGroundLocalY(sprite.position.x, sprite.position.z, 0)
            group.position.set(sprite.position.x, baseGroundY + 0.01, sprite.position.z)
          }
          group.scale.set(scale, scale, scale)
          group.children.forEach(child => {
            if (child.material && child.material.opacity !== undefined) {
              child.material.opacity = 0.3 + 0.2 * pulse
            }
          })
        })
      }
      if (this.waterUniforms) this.waterUniforms.uTime.value = t
      if (this._poolWaterUniforms) this._poolWaterUniforms.uTime.value = t
      if (this._sandTerrainUniforms && this.dirLight) {
        this._sandTerrainUniforms.uTime.value = t
        this._sandTerrainUniforms.uLightDir.value.copy(this.dirLight.position).normalize()
        this._sandTerrainUniforms.uSunColor.value
          .copy(this.dirLight.color)
          .multiplyScalar(this.dirLight.intensity)
      }
      if (this.precipMode === 'rain') this._updateRainPoints(dt)
      if (this.precipMode === 'snow') this._updateSnowPoints(dt, t)
      if (this._tourTransition) {
        this._updateTourCameraTransition(dt)
      } else if (this.isTourModeActive()) {
        this._updateTour(dt)
      } else if (this.sandboxRoot && this._orbit && this.camera) {
        this.camera.up.copy(this._axisY)
        this.camera.lookAt(this._orbit.target)
      }
      const holdTerrainBrush =
        this._pointer &&
        this._pointer.mode === 'terrain' &&
        (this.terrainTool === 'sculpt' ||
          this.terrainTool === 'water' ||
          this.terrainTool === 'flatten' ||
          this.terrainTool === 'material') &&
        this._pointer.terrainLastOk &&
        this._sandSculptMesh
      const sculptHadPointerMove = this._sculptMovedThisFrame
      this._sculptMovedThisFrame = false
      if (holdTerrainBrush && !sculptHadPointerMove) {
        const refDt = 0.016
        const k = dt / refDt
        if (this.terrainTool === 'sculpt') {
          this._trySculptAtWorld(this._terrainStroke, 0.42 * k)
        } else if (this.terrainTool === 'water') {
          this._tryWaterAtWorld(this._terrainStroke, 0.55 * k)
        } else if (this.terrainTool === 'flatten') {
          this._tryFlattenAtWorld(this._terrainStroke, 0.36 * k)
        } else if (this.terrainTool === 'material') {
          this._tryMaterialAtWorld(this._terrainStroke, 0.42 * k)
        }
      }
      if (this._sandPropsGroup) updateSandPropAnimations(this._sandPropsGroup, dt)
      this._flushSideRebuild()
      this._flushNormals()
      this._flushWaterSync()
      this._syncSandSculptSideVisibility()
      this.renderer.render(this.scene, this.camera)
    },

    _capturePlanformBase64 () {
      if (!this.renderer || !this.camera || !this.scene || !this.sandboxRoot) return ''
      const size = 360
      const rt = new THREE.WebGLRenderTarget(size, size, {
        minFilter: THREE.LinearFilter,
        magFilter: THREE.LinearFilter,
        format: THREE.RGBAFormat
      })
      const savedPos = this.camera.position.clone()
      const savedQuat = this.camera.quaternion.clone()
      const savedUp = this.camera.up.clone()
      const savedAspect = this.camera.aspect
      const target = this.getGroundCenter(new THREE.Vector3())
      this.camera.position.set(target.x, target.y + 14, target.z)
      this.camera.up.set(0, 0, -1)
      this.camera.lookAt(target)
      this.camera.aspect = 1
      this.camera.updateProjectionMatrix()
      this.camera.updateMatrixWorld()

      const prevTarget = this.renderer.getRenderTarget()
      this.renderer.setRenderTarget(rt)
      this.renderer.render(this.scene, this.camera)
      this.renderer.setRenderTarget(prevTarget)

      const pixels = new Uint8Array(size * size * 4)
      this.renderer.readRenderTargetPixels(rt, 0, 0, size, size, pixels)

      const canvas = document.createElement('canvas')
      canvas.width = size
      canvas.height = size
      const ctx = canvas.getContext('2d')
      let dataUrl = ''
      if (ctx) {
        const imageData = ctx.createImageData(size, size)
        for (let y = 0; y < size; y++) {
          for (let x = 0; x < size; x++) {
            const srcI = ((size - 1 - y) * size + x) * 4
            const dstI = (y * size + x) * 4
            imageData.data[dstI] = pixels[srcI]
            imageData.data[dstI + 1] = pixels[srcI + 1]
            imageData.data[dstI + 2] = pixels[srcI + 2]
            imageData.data[dstI + 3] = 255
          }
        }
        ctx.putImageData(imageData, 0, 0)
        try {
          dataUrl = canvas.toDataURL('image/jpeg', 0.55)
        } catch (err) {
          dataUrl = ''
        }
      }

      this.camera.position.copy(savedPos)
      this.camera.quaternion.copy(savedQuat)
      this.camera.up.copy(savedUp)
      this.camera.aspect = savedAspect
      this.camera.updateProjectionMatrix()
      this.camera.updateMatrixWorld()
      rt.dispose()
      return dataUrl
    },

    /** 公开方法：捕获沙盘截屏并返回 File 对象（用于上传） */
    captureScreenAsFile (filename = 'sandplay_screenshot.jpg') {
      const dataUrl = this._capturePlanformBase64()
      if (!dataUrl) return Promise.resolve(null)
      return new Promise((resolve) => {
        const img = new Image()
        img.onload = () => {
          const canvas = document.createElement('canvas')
          canvas.width = img.width
          canvas.height = img.height
          const ctx = canvas.getContext('2d')
          if (!ctx) {
            resolve(null)
            return
          }
          ctx.drawImage(img, 0, 0)
          canvas.toBlob((blob) => {
            if (!blob) {
              resolve(null)
              return
            }
            const file = new File([blob], filename, { type: 'image/jpeg' })
            resolve(file)
          }, 'image/jpeg', 0.85)
        }
        img.onerror = () => resolve(null)
        img.src = dataUrl
      })
    },

    /** 地形 sculpt 网格分段（X/Z 可能不同，须与 PlaneGeometry 一致） */
    _getSculptGridSegs () {
      const segX = this._sculptSegX != null ? this._sculptSegX : SCULPT_GRID_SEG
      const segZ = this._sculptSegZ != null ? this._sculptSegZ : SCULPT_GRID_SEG
      return { segX, segZ, cols: segX + 1 }
    },

    /** 后端 terrainData 实体通常只持久化 n/h，水面 w 与材质 m 需额外写入 code */
    _collectTerrainOverlay () {
      const terrainWet = []
      const terrainMat = []
      const geo = this._sandSculptGeo
      if (!geo) return { terrainWet, terrainMat }
      const wet = geo.attributes.wetness
      const mat = geo.attributes.matType
      const warr = wet ? wet.array : null
      const marr = mat ? mat.array : null
      const { segX, segZ, cols } = this._getSculptGridSegs()
      for (let iz = 0; iz <= segZ; iz++) {
        for (let ix = 0; ix <= segX; ix++) {
          const i = iz * cols + ix
          const key = ix + '_' + iz
          if (warr && warr[i] > 0.001) {
            terrainWet.push({ n: key, w: safeFixed(warr[i], 3) })
          }
          if (marr && marr[i] > 0.001) {
            terrainMat.push({ n: key, m: String(Math.round(marr[i])) })
          }
        }
      }
      return { terrainWet, terrainMat }
    },

    _collectTerrainData () {
      const terrainData = []
      const geo = this._sandSculptGeo
      if (!geo) return terrainData
      const pos = geo.attributes.position
      if (!pos) return terrainData
      const wet = geo.attributes.wetness
      const mat = geo.attributes.matType
      const arr = pos.array
      const warr = wet ? wet.array : null
      const marr = mat ? mat.array : null
      const { segX, segZ, cols } = this._getSculptGridSegs()
      for (let iz = 0; iz <= segZ; iz++) {
        for (let ix = 0; ix <= segX; ix++) {
          const i = iz * cols + ix
          const i3 = i * 3
          const rawY = arr[i3 + 1]
          const hVal = parseFloat(safeFixed(rawY, 4))
          const wVal = warr ? warr[i] : 0
          const mVal = marr ? marr[i] : 0
          const hasW = warr && warr[i] > 0.001
          const hasM = marr && marr[i] > 0.001
          if (Math.abs(hVal) < 0.001 && !hasW && !hasM) continue
          const pt = { n: ix + '_' + iz, h: encodeTerrainHeightForSave(hVal, wVal, mVal) }
          if (hasW) pt.w = safeFixed(warr[i], 3)
          if (hasM) pt.m = String(Math.round(marr[i]))
          terrainData.push(pt)
        }
      }
      return terrainData
    },

    _collectTerrainRatios () {
      const geo = this._sandSculptGeo
      if (!geo) return { lakesRatio: '0', mountainRatio: '0', flatRatio: 0 }
      const pos = geo.attributes.position
      const wet = geo.attributes.wetness
      if (!pos) return { lakesRatio: '0', mountainRatio: '0', flatRatio: 0 }
      const n = pos.count
      let wetCount = 0
      let mountainCount = 0
      let flatCount = 0
      const wetArr = wet ? wet.array : null
      const arr = pos.array
      const FLAT_EPS = 0.01
      for (let i = 0; i < n; i++) {
        const h = arr[i * 3 + 1]
        if (wetArr && wetArr[i] > 0.08) wetCount += 1
        if (h > SCULPT_MAX_Y * 0.35) mountainCount += 1
        if (Math.abs(h) <= FLAT_EPS) flatCount += 1
      }
      return {
        lakesRatio: n ? (wetCount / n).toFixed(3) : '0',
        mountainRatio: n ? (mountainCount / n).toFixed(3) : '0',
        flatRatio: n ? flatCount / n : 0
      }
    },

    _collectSandPropRows () {
      const modeData = []
      const allModelData = []
      if (!this._sandPropsGroup) {
        return { modeData, allModelData }
      }
      const children = this._sandPropsGroup.children.slice()
      children.sort((a, b) => {
        const ao = (a.userData.sandProp && a.userData.sandProp.putDownOrder) || 0
        const bo = (b.userData.sandProp && b.userData.sandProp.putDownOrder) || 0
        return ao - bo
      })
      let prevPlacedAt = 0
      children.forEach((prop, index) => {
        const meta = prop.userData.sandProp || {}
        const st = prop.userData.editState || {}
        const category = meta.category || 'character'
        const typeInfo = SAND_CATEGORY_MAP[category] || SAND_CATEGORY_MAP.character
        const placedAt = meta.placedAt || Math.floor(Date.now() / 1000)
        const timeInterval =
          index === 0 || !prevPlacedAt ? 0 : Math.max(0, (placedAt - prevPlacedAt) * 1000)
        prevPlacedAt = placedAt
        const num = String((meta.putDownOrder || index + 1)).padStart(3, '0')
        const row = {
          num,
          name: meta.label || '',
          typeNum: typeInfo.num,
          typeName: typeInfo.name,
          weather: '01',
          siblingStatus: '0',
          modelPower: '80',
          modelDetails: meta.label || '',
          modelTime: String(placedAt),
          putDownOrder: String(meta.putDownOrder || index + 1),
          timeInterval,
          pos: {
            x: safeFixed(prop.position.x, 2),
            y: safeFixed(prop.position.y, 2),
            z: safeFixed(prop.position.z, 2)
          },
          rota: {
            x: String(Math.round(((st.pitch || 0) * 180) / Math.PI)),
            y: String(Math.round(((st.rotation || 0) * 180) / Math.PI)),
            z: '0'
          }
        }
        modeData.push(row)
        allModelData.push({
          modelId: 'model_' + String(meta.id || index + 1).padStart(3, '0'),
          modelType: typeInfo.num,
          modelPower: '80',
          choosed: '1',
          deleted: '0',
          deleteTime: '0',
          modelDetails: meta.label || ''
        })
      })
      return { modeData, allModelData }
    },

    _clearAllSandProps () {
      if (!this._sandPropsGroup) return
      const children = this._sandPropsGroup.children.slice()
      children.forEach((child) => {
        this._removePropIndicatorFor(child)
        disposeSandProp(child)
        this._sandPropsGroup.remove(child)
      })
    },

    _terrainIndexFromKey (key, segX, segZ) {
      const parts = String(key).split('_')
      if (parts.length !== 2) return -1
      const ix = parseInt(parts[0], 10)
      const iz = parseInt(parts[1], 10)
      const sx = segX != null ? segX : SCULPT_GRID_SEG
      const sz = segZ != null ? segZ : SCULPT_GRID_SEG
      if (isNaN(ix) || isNaN(iz) || ix < 0 || iz < 0 || ix > sx || iz > sz) return -1
      return iz * (sx + 1) + ix
    },

    _applyTerrainData (terrainData, overlay = {}) {
      const geo = this._sandSculptGeo
      const wetList = overlay.terrainWet || []
      const matList = overlay.terrainMat || []
      const hasTerrain = terrainData && terrainData.length > 0
      const hasOverlay = wetList.length > 0 || matList.length > 0
      if (!geo || (!hasTerrain && !hasOverlay)) return
      const pos = geo.attributes.position
      const wet = geo.attributes.wetness
      const mat = geo.attributes.matType
      const color = geo.attributes.color
      if (!pos) return
      const arr = pos.array
      const warr = wet ? wet.array : null
      const marr = mat ? mat.array : null
      const carr = color ? color.array : null
      const { segX, segZ } = this._getSculptGridSegs()
      const map = {}
      if (hasTerrain) {
        terrainData.forEach((pt) => {
          if (pt && pt.n != null) map[pt.n] = pt
        })
      }

      if (warr) warr.fill(0)
      if (marr) marr.fill(0)
      if (carr) {
        for (let i = 0; i < carr.length; i++) carr[i] = 1
      }

      let changed = false
      let wetChanged = false
      let matChanged = false
      let colorChanged = !!carr

      const applyPoint = (pt) => {
        if (!pt || pt.n == null) return
        const i = this._terrainIndexFromKey(pt.n, segX, segZ)
        if (i < 0) return
        const i3 = i * 3
        if (pt.h != null && !isNaN(parseFloat(pt.h))) {
          const decoded = decodeTerrainHeightFromSave(parseFloat(pt.h))
          arr[i3 + 1] = decoded.h
          changed = true
          if (warr && decoded.w > 0.001) {
            warr[i] = Math.max(warr[i] || 0, decoded.w)
            wetChanged = true
          }
          if (marr && decoded.m > 0) {
            marr[i] = Math.max(marr[i] || 0, decoded.m)
            matChanged = true
          }
        }
        if (warr && pt.w != null && !isNaN(parseFloat(pt.w))) {
          warr[i] = Math.max(warr[i] || 0, Math.min(1, parseFloat(pt.w)))
          wetChanged = true
        }
        if (marr && pt.m != null && !isNaN(parseFloat(pt.m))) {
          const mv = Math.max(0, Math.min(4, parseFloat(pt.m)))
          if (mv > 0) {
            marr[i] = mv
            matChanged = true
          }
        }
      }

      Object.keys(map).forEach((key) => applyPoint(map[key]))
      wetList.forEach((pt) => applyPoint(pt))
      matList.forEach((pt) => applyPoint(pt))

      if (warr) {
        let prunedWet = false
        for (let i = 0; i < warr.length; i++) {
          if (warr[i] > 0 && warr[i] < 0.06) {
            warr[i] = 0
            prunedWet = true
          }
        }
        if (prunedWet) wetChanged = true
      }

      if (!changed && !wetChanged && !matChanged) return
      pos.needsUpdate = true
      if (wetChanged && wet) wet.needsUpdate = true
      if (matChanged && mat) mat.needsUpdate = true
      if (colorChanged && color) color.needsUpdate = true
      geo.computeVertexNormals()
      this._notifyTerrainVisualChange()
    },

    async _restoreSandPropRow (row) {
      if (!row || !this._sandPropsGroup) return
      const found = findSandCatalogItem(row.typeNum, row.name)
      if (!found) return
      const placeItem = {
        key: buildSandItemKey(found.tab, found.item),
        label: found.item.label,
        emoji: found.item.emoji,
        modelKey: found.item.modelKey,
        modelUrl: found.item.modelUrl,
        thumbUrl: found.item.thumbUrl,
        previewUrl: found.item.previewUrl,
        category: found.tab
      }
      const prop = await this._createSandPropObject(placeItem)
      if (!prop) return
      const px = parseFloat(row.pos && row.pos.x)
      const py = parseFloat(row.pos && row.pos.y)
      const pz = parseFloat(row.pos && row.pos.z)
      if (!isNaN(px)) prop.position.x = px
      if (!isNaN(py)) prop.position.y = py
      if (!isNaN(pz)) prop.position.z = pz
      this._initSandPropUserData(prop, placeItem)
      if (row.putDownOrder) {
        prop.userData.sandProp.putDownOrder = parseInt(row.putDownOrder, 10) || 0
      }
      if (row.modelTime) {
        prop.userData.sandProp.placedAt = parseInt(row.modelTime, 10) || 0
      }
      const st = prop.userData.editState
      const rotaY = parseFloat(row.rota && row.rota.y)
      const rotaX = parseFloat(row.rota && row.rota.x)
      if (!isNaN(rotaY)) st.rotation = (rotaY * Math.PI) / 180
      if (!isNaN(rotaX)) st.pitch = (rotaX * Math.PI) / 180
      this._clampPropSandboxXZ(prop.position)
      this._sandPropsGroup.add(prop)
      this._applyPropTransform(prop, { resetPose: true })
    },

    /** 回显已保存沙盘 */
    async importSandTableScene (saved) {
      if (!saved || this.disposed) return null
      this.endPropEdit(true)

      let codeMeta = {}
      try {
        if (saved.code) {
          codeMeta = typeof saved.code === 'string' ? JSON.parse(saved.code) : saved.code
        }
      } catch (e) {
        codeMeta = {}
      }

      if (saved.weather) {
        this.applyWeather(saved.weather)
      } else if (codeMeta.weather) {
        this.applyWeather(codeMeta.weather)
      }

      if (codeMeta.terrainVisible != null && this.terrainGroup) {
        this.terrainGroup.visible = !!codeMeta.terrainVisible
      }

      this._applyTerrainData(saved.terrainData || [], {
        terrainWet: saved.terrainWet || codeMeta.terrainWet || [],
        terrainMat: saved.terrainMat || codeMeta.terrainMat || []
      })
      this._clearAllSandProps()

      const modeData = saved.modeData || []
      const restoreModelRefs = []
      modeData.forEach((row) => {
        const found = findSandCatalogItem(row.typeNum, row.name)
        if (!found) return
        const ref = found.item.modelKey || found.item.modelUrl
        if (ref) restoreModelRefs.push(ref)
      })
      if (restoreModelRefs.length) {
        await preloadSandModels(restoreModelRefs, { concurrency: 3 })
      }

      for (let i = 0; i < modeData.length; i++) {
        await this._restoreSandPropRow(modeData[i])
      }
      this._reapplyAllPropTransforms()

      return {
        weather: saved.weather || codeMeta.weather || this.weather,
        terrainMaterial: saved.terrainMaterial || codeMeta.terrainMaterial,
        terrainVisible: codeMeta.terrainVisible,
        codingTime: saved.codingTime
      }
    },

    /** 导出沙盘存档所需场景数据 */
    exportSandTableScene () {
      this.endPropEdit(true)
      const terrainData = this._collectTerrainData()
      const terrainOverlay = this._collectTerrainOverlay()
      const ratios = this._collectTerrainRatios()
      const { modeData, allModelData } = this._collectSandPropRows()
      const codeMeta = {
        weather: this.weather,
        terrainMaterial: this.terrainMaterial,
        terrainVisible: this.terrainVisible
      }
      if (terrainOverlay.terrainWet.length) {
        codeMeta.terrainWet = terrainOverlay.terrainWet
      }
      if (terrainOverlay.terrainMat.length) {
        codeMeta.terrainMat = terrainOverlay.terrainMat
      }
      const code = JSON.stringify(codeMeta)
      return {
        terrainData,
        modeData,
        allModelData,
        planform: '',
        thumbnail: '',
        lakesRatio: ratios.lakesRatio,
        mountainRatio: ratios.mountainRatio,
        flatRatio: ratios.flatRatio,
        code
      }
    },

    dispose () {
      this.disposed = true
      this._hidePropIndicator()
      this._cancelTourCameraTransition()
      this._unbindTourKeys()
      this._unbindTourPointerLock()
      window.removeEventListener('resize', this.onResize)
      this._vertGrid = null
      if (this.ro) {
        this.ro.disconnect()
        this.ro = null
      }
      cancelAnimationFrame(this.raf)

      if (this.renderer && this.renderer.domElement) {
        const c = this.renderer.domElement
        c.removeEventListener('pointerdown', this._onPointerDown)
        c.removeEventListener('pointermove', this._onPointerMove)
        c.removeEventListener('pointerup', this._onPointerUp)
        c.removeEventListener('pointercancel', this._onPointerUp)
        c.removeEventListener('lostpointercapture', this._onPointerUp)
        c.removeEventListener('contextmenu', this._onContextMenu)
      }

      if (this.scene) {
        this.scene.traverse((obj) => {
          if (obj.isMesh) {
            if (obj.geometry) obj.geometry.dispose()
            if (obj.material) {
              const mats = Array.isArray(obj.material)
                ? obj.material
                : [obj.material]
              mats.forEach((m) => {
                if (m.map) m.map.dispose()
                m.dispose()
              })
            }
          } else if (obj.isPoints) {
            if (obj.geometry) obj.geometry.dispose()
            if (obj.material) obj.material.dispose()
          } else if (obj.isSprite) {
            if (obj.material) {
              if (obj.material.map) obj.material.map.dispose()
              obj.material.dispose()
            }
          }
        })
        this.scene = null
      }

      if (this.renderer) {
        this.renderer.dispose()
        if (this.renderer.domElement && this.renderer.domElement.parentNode) {
          this.renderer.domElement.parentNode.removeChild(this.renderer.domElement)
        }
        this.renderer = null
      }
      this.camera = null
      this.waterUniforms = null
      this._sandTerrainUniforms = null
      this.terrainGroup = null
      this.sandboxRoot = null
      this.hemiLight = null
      this.dirLight = null
      this.precipMode = null
      this.rainPoints = null
      this.snowPoints = null
      this._pointer = null
      this._orbit = null
    }
  }
}
</script>

<style scoped>
.island-playground-three {
  position: absolute;
  inset: 0;
  overflow: hidden;
}

.island-playground-three >>> canvas {
  display: block;
  width: 100% !important;
  height: 100% !important;
  touch-action: none;
  cursor: default;
}

@media (pointer: coarse) {
  .island-playground-three >>> canvas {
    cursor: auto;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/IslandPlaygroundThree.vue